import { IF, nil, NO, UIColor, UIRectangle, UIRoute, UITextView, UIView, UIViewController, YES } from "uicore-ts"
import { RHProcurement, RHProcurementOffer, RHProcurementPublic } from "../../../models/prodModels"
import { CompanyViewController } from "../CompanyView/CompanyViewController"
import { RHContractsView } from "../ContractsView/RHContractsView"
import { RHCore } from "../Core/RHCore"
import { RHProcurementAmendmentPresentation } from "../Core/RHProcurementAmendmentPresentation"
import { RHProcurementDeadlineAutoExtensionPresentation } from "../Core/RHProcurementDeadlineAutoExtensionPresentation"
import {
    RHProcurementAmendedPayload,
    RHProcurementDeadlineChangedPayload,
    RHProcurementInformationResult
} from "../Core/RHProcurementStore"
import { CBDialogViewShower } from "../Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHAlertBannerView } from "../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHBreadcrumbView, type RHBreadcrumbItem } from "../Custom components/RH/RHBreadcrumbView"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHConflictOfInterestBannerView } from "../Custom components/RH/RHConflictOfInterestBannerView"
import { RHNotificationBadge } from "../Custom components/RH/RHNotificationBadge"
import { RHSegmentedTabBarView } from "../Custom components/RH/RHSegmentedTabBarView"
import { RHTeamUserReferenceResolver } from "../Custom components/RH/RHTeamRoleSectionView"
import { RHButtons, RHColors, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHNavigationResettable } from "../Custom components/RH/Utils_slop/RHNavigationResettable"
import { RHProcurementLifecyclePresentation } from "../Custom components/RH/Utils_slop/RHProcurementLifecyclePresentation"
import { RHProcurementWinnerStatus } from "../Custom components/RH/Utils_slop/RHProcurementWinnerStatus"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHProcurementDetailsViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { LoginViewController } from "../Login/LoginViewController"
import { NewProcurementViewController } from "../NewProcurement/NewProcurementViewController"
import {
    ProcurementTeamView,
    type RHTeamSelfAdditionRole
} from "../NewProcurement/ProcurementTeamView"
import { SettingsViewController } from "../Settings/SettingsViewController"
import { ProcurementSearchViewController } from "../Search/ProcurementSearchViewController"
import type { RHProcurementExcelExportButtonState } from "../ProcurementExcelExport/ProcurementExcelExportButtonView"
import { ProcurementExcelExportButtonView } from "../ProcurementExcelExport/ProcurementExcelExportButtonView"
import { ProcurementExcelExportDownloader } from "../ProcurementExcelExport/ProcurementExcelExportDownloader"
import type {
    RHOfferCapabilities,
    RHOfferCreationCapabilities,
    RHOffererSubmissionOutcome,
    RHOfferNotificationSnapshot,
    RHProcurementCapabilities,
    RHProcurementEngagementSummary,
    RHProcurementExcelExport,
    RHProcurementOpeningProtocolSharing,
    RHProcurementInformativeStatus,
    RHProcurementLifecycleState,
    RHProcurementOfferStateSnapshot,
    RHProcurementTeam,
    RHReconciliationRoundSummary
} from "../SocketClientFunctions"
import { UserMainViewController } from "../UserMain/UserMainViewController"
import { CreateReconciliationProcurementDialogView } from "./EvaluateOffers/CreateReconciliationProcurementDialogView"
import { RHOffersEvaluationView } from "./EvaluateOffers/RHOffersEvaluationView"
import { MakeOfferView, type RHMakeOfferLoadResult } from "./MakeOffer/MakeOfferView"
import { RHAskQuestionDialogView } from "./QandA/RHAskQuestionDialogView"
import { RHProcurerMessageDialogView } from "./QandA/RHProcurerMessageDialogView"
import { RHQAView } from "./QandA/RHQAView"
import { RHProcurementClosureDialogView } from "./RHProcurementClosureDialogView"
import { RHProcurementDetailsView } from "./RHProcurementDetailsView"
import { InviteCompaniesToProcurementDialogView } from "./InviteCompaniesToProcurementDialogView"
import { ProcurementRoundContext, type ProcurementRound } from "./ProcurementRoundContext"
import { ProcurementRoundWorkspaceView, type ProcurementRoundTab } from "./ProcurementRoundWorkspaceView"
import { referenceId } from "../Core/RHReference"
import { broadcastPayloadOf } from "../Core/RHBroadcastPayload"
import { messageOfError } from "../Core/RHErrorMessage"


type ProcurementTab = "details" | "team" | "makeOffer" | "offers" | "qa" | "contracts"

type RHProcurementOfferTabState = "empty" | "draft" | "submitted"

// A procurement from the owner's side carries the tenderers list that the public form leaves out.
function isFullProcurement(procurement: RHProcurementPublic | RHProcurement): procurement is RHProcurement {
    return "registeredTenderers" in procurement
}


export class ProcurementDetailsViewController extends UIViewController implements RHNavigationResettable {
    
    // Configuration
    readonly sidePanelWidth = 700
    
    // Header
    readonly breadcrumbView = new RHBreadcrumbView().addedAsSubviewToView(this.view)

    readonly titleLabel = new UITextView(
        this.view.elementID + "TitleLabel",
        UITextView.type.header4
    ).configuredWithObject({
        text: "Hanke üldandmed",
        backgroundColor: UIColor.transparentColor,
        textColor: RHColors.textPrimary,
        textAlignment: UITextView.textAlignment.center,
        // A long procurement name ("Hanke \"…\" toimingud") must wrap instead of
        // being clipped at the content edge — the layout sizes the title to its
        // wrapped height (see layoutViewSubviews).
        isSingleLine: NO
    }).addedAsSubviewToView(this.view)

    readonly backButton = new RHButton().configuredWithObject({
        titleLabel: { text: "\u2190 Tagasi" },
        variant: "quietWarning",
        contentPadding: RHButtons.compactContentPadding,
        style: {
            fontSize: RHButtons.compactFontSize
        },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: async () => {
                const encodedReturnRoute = UIRoute.currentRoute
                    .componentWithViewController(ProcurementDetailsViewController)?.parameters.returnRoute
                if (encodedReturnRoute) {
                    try {
                        // UIRoute parses separators after decoding, so nested routes use Base64.
                        new UIRoute(atob(encodedReturnRoute)).apply()
                        return
                    } catch (error) {
                        console.warn("Procurement return route could not be restored", error)
                    }
                }
                const backTo = UIRoute.currentRoute
                    .componentWithViewController(ProcurementDetailsViewController)
                    ?.parameters.backTo as string | undefined
                
                if (backTo) {
                    UIRoute.currentRoute
                        .routeByRemovingComponentNamed(ProcurementDetailsViewController.routeComponentName)
                        .routeWithComponent(backTo, {})
                        .apply()
                }
                else {
                    window.history.back()
                }
            }
        }
    }).addedAsSubviewToView(this.view)

    _reconciliationRoundChain: RHReconciliationRoundSummary[] = []
    _roundContext = new ProcurementRoundContext()
    _roundWorkspace?: ProcurementRoundWorkspaceView

    readonly registerAsTendererButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Registreeru hankele" },
        variant: "success",
        contentPadding: RHButtons.compactContentPadding,
        style: {
            fontSize: RHButtons.compactFontSize
        },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => {
                this._confirmRegisterAsTenderer()
            }
        }
    })
    
    // Unaffiliated banner — shown when the user is logged in but has no company affiliation
    readonly unaffiliatedExplanationLabel = new UITextView().configuredWithObject({
        text: "Pakkumuse esitamiseks või teabevahetuse nägemiseks pead olema seotud mõne ettevõttega.",
        backgroundColor: UIColor.transparentColor,
        textAlignment: UITextView.textAlignment.center
    })
    
    readonly unaffiliatedButton = new RHButton().configuredWithObject({
        variant: RHProcurementDetailsViewTokens.promptActionButtonVariant,
        contentPadding: RHButtons.compactContentPadding,
        style: {
            fontSize: RHButtons.compactFontSize
        },
        titleLabel: { text: "Liitu või registreeri ettevõte" },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => {
                UIRoute.currentRoute
                    .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
                    .routeWithViewControllerComponent(CompanyViewController, nil)
                    .apply()
            }
        }
    })
    
    // Log in banner — shown when the user is not authenticated
    readonly loginExplanationLabel = new UITextView().configuredWithObject({
        text: "Pakkumuse esitamiseks või teabevahetuse nägemiseks logi sisse.",
        backgroundColor: UIColor.transparentColor,
        textAlignment: UITextView.textAlignment.center
    })
    
    readonly loginButton = new RHButton().configuredWithObject({
        variant: RHProcurementDetailsViewTokens.promptActionButtonVariant,
        contentPadding: RHButtons.compactContentPadding,
        style: {
            fontSize: RHButtons.compactFontSize
        },
        titleLabel: { text: "Logi sisse" },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => {
                UIRoute.currentRoute
                    .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
                    .routeWithViewControllerComponent(LoginViewController, nil)
                    .apply()
            }
        }
    })
    
    // Conflict-of-interest confirmation prompt — shown in procurer mode when the current
    // procurement team member must confirm they have no conflict of interest before the
    // procurement's detailed data (submitted offers + Q&A) is unlocked (canViewDetailed).
    // Uses the same on-brand RHAlertBannerView as the offerer-side OfferTeamView so both
    // roles get an identical confirmation experience.
    readonly conflictConfirmationBanner = new RHAlertBannerView().configuredWithObject({
        alertType: RHAlertBannerView.type.Warning,
        text: "Pakkumuste ja teabevahetuse nägemiseks pead kinnitama, et Sul puudub selle hankega seotud huvide konflikt. Pärast kinnitamist avanevad „Pakkumused“ ja „Teabevahetus“ vahelehed."
    })
    
    readonly procurementClosureBanner = new RHAlertBannerView().configuredWithObject({
        alertType: RHAlertBannerView.type.Warning,
        textPresentation: RHAlertBannerView.textPresentation.Outcome,
        text: "Hange on lõpetatud ilma lepinguta"
    })

    readonly offerAccessBanner = new RHAlertBannerView().configuredWithObject({
        alertType: RHAlertBannerView.type.Warning
    })

    readonly procurementUnavailableBanner = new RHAlertBannerView().configuredWithObject({
        alertType: RHAlertBannerView.type.Error,
        textPresentation: RHAlertBannerView.textPresentation.Standard,
        textAlignment: UITextView.textAlignment.center,
        text:
            "<strong>Hange ei ole sulle kättesaadav. See võib olla mitteavalik hange, millele sinu ettevõtet ei ole kutsutud, või link ei ole enam kehtiv.</strong><br><br>" +
            "Avalik hange on nähtav hangete otsingus ning sellega saavad tutvuda ja pakkumuse esitada kõik huvitatud ettevõtjad.<br>" +
            "Mitteavalik hange ei ole hangete otsingus nähtav ja teised ettevõtjad seda ei näe. Pakkumuse esitamise ettepanek saadetakse ainult sinu valitud ettevõtjatele."
    })

    readonly conflictConfirmationButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Kinnitan huvide konflikti puudumist" },
        variant: "success",
        contentPadding: RHButtons.compactContentPadding,
        style: {
            fontSize: RHButtons.compactFontSize
        },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => {
                this._confirmConflictOfInterest()
            }
        }
    })

    // Cross-company conflict-of-interest banner (ConflictOfInterestService) — a
    // different mechanism from conflictConfirmationBanner above: this one is
    // server-derived, not a self-attestation, and offers disclosure/recusal actions.
    readonly conflictOfInterestBannerView = new RHConflictOfInterestBannerView()

    // Ask question button — only shown in offerer mode when registered
    readonly askQuestionButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Esita küsimus" },
        variant: RHProcurementDetailsViewTokens.askQuestionButtonVariant,
        contentPadding: RHButtons.compactContentPadding,
        style: {
            fontSize: RHButtons.compactFontSize
        },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: async () => {
                this._showAskQuestionDialog()
            }
        }
    })
    
    // Send-message button — only shown to the procuring company (procurer) on the Q&A tab.
    // Mirrors askQuestionButton's design/placement; the offerer and procurer variants are
    // mutually exclusive (one occupies the slot above the Q&A columns per role).
    readonly sendMessageButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Saada sõnum" },
        variant: RHProcurementDetailsViewTokens.askQuestionButtonVariant,
        contentPadding: RHButtons.compactContentPadding,
        style: {
            fontSize: RHButtons.compactFontSize
        },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: async () => {
                this._showProcurerMessageDialog()
            }
        }
    })
    
    readonly tabBar = new RHSegmentedTabBarView([
        { key: "details", title: "Üldandmed" },
        { key: "team", title: "Hankemeeskond" },
        {
            key: "makeOffer",
            title: "Registreeru hankele",
            unselectedVariant: "success"
        },
        { key: "offers", title: "Pakkumused" },
        { key: "qa", title: "Teabevahetus" },
        { key: "contracts", title: "Lepingud" }
    ]).configuredWithObject({
        selectionDidChange: (key) => {
            if (key === "makeOffer" && this._shouldShowRegisterAsTendererButton) {
                this.tabBar.selectedKey = this._currentTab
                this._confirmRegisterAsTenderer()
                return
            }
            this._showTab(key as ProcurementTab)
        }
    })

    readonly detailsTabButton = this.tabBar.tabButtonForKey("details")!
    readonly teamTabButton = this.tabBar.tabButtonForKey("team")!
    readonly makeOfferTabButton = this.tabBar.tabButtonForKey("makeOffer")!
    readonly offersTabButton = this.tabBar.tabButtonForKey("offers")!
    readonly qaTabButton = this.tabBar.tabButtonForKey("qa")!
    readonly contractsTabButton = this.tabBar.tabButtonForKey("contracts")!
    
    readonly askQuestionDialog = new RHAskQuestionDialogView()
    readonly procurerMessageDialog = new RHProcurerMessageDialogView()
    readonly closureDialog = new RHProcurementClosureDialogView()
    
    // State
    
    /**
     * The loaded procurement. May be the full non-public RHProcurement when isCurrentUserOnProcuringSide is YES
     * or the public RHProcurementPublic (for all other authenticated and anonymous visitors).
     */
    _procurement?: RHProcurementPublic

    /** Hanke seisundid ja filtrid v3.1 — server-resolved, shared with both dashboards. */
    _procurementLifecycleState?: RHProcurementLifecycleState
    _procurerInformativeStatus?: RHProcurementInformativeStatus
    _offererInformativeStatus?: RHProcurementInformativeStatus
    _offererSubmissionOutcome?: RHOffererSubmissionOutcome

    _showsProcurementUnavailableState = NO
    
    /**
     * True when the server returns procuring-side information for the current user.
     * Detailed access is determined separately by the procurement capabilities.
     */
    _isCurrentUserOnProcuringSide = NO
    
    /**
     * The current company's offers for this procurement (populated for non-owner
     * authenticated users). Empty for guests and owners.
     */
    _userOffers: RHProcurementOfferStateSnapshot[] = []

    _offerCapabilities?: RHOfferCapabilities

    _offerCreationCapabilities?: RHOfferCreationCapabilities

    _canWithdrawTendererRegistration = NO
    
    /**
     * Drives the offerer tab title:
     * - unregistered → "Registreeru hankele"
     * - empty        → "Koosta pakkumus"
     * - draft     → "Jätka koostamist"
     * - submitted → "Minu pakkumus"
     */
    _makeOfferTabState: RHProcurementOfferTabState = "empty"
    
    /**
     * Unseen Teabevahetus notification count for this procurement
     * (question.asked / question.answered / procurerBroadcast.sent).
     * Drives the qaTabButton badge.
     */
    private _unseenQANotificationCount = 0
    
    /**
     * Unseen offer notification count for this procurement (offer.submitted for procurers;
     * opening, result and part-closure events for offerers). Drives the offersTabButton badge.
     */
    private _unseenOfferNotificationCount = 0

    /** Unseen published-procurement amendment or whole-closure events for the loaded procurement. */
    _unseenProcurementNotificationCount = 0
    
    /**
     * Set to YES when the procurement is published and has at least one unanswered question.
     * Drives the qaTabButton attention badge.
     */
    private _requiresProcurerQAAttention = NO
    
    /**
     * Set to YES when the procurement deadline has passed, there are submitted offers,
     * and the evaluation has not yet been finalized. Drives the offersTabButton
     * attention badge and the trail from the nav button inward.
     */
    private _requiresProcurerAttention = NO
    
    _procurementCapabilities?: RHProcurementCapabilities
    _companyInvitationDialog?: InviteCompaniesToProcurementDialogView
    readonly procurementAccessExplanationLabel = new UITextView().configuredWithObject({
        text: "Mitteavaliku hanke vaatamiseks logi sisse ja vali kutsutud ettevõte. " +
            "Kui sul puudub seos kutsutud ettevõttega, liitu ettevõttega või registreeri see keskkonnas.",
        isSingleLine: NO
    })
    _procurementEngagementSummary?: RHProcurementEngagementSummary
    
    /**
     * The active company ID the make-offer view was last loaded for, or
     * undefined if it has never been loaded. Guards against reloading it a
     * second time for the same company (e.g. when the user switches to
     * offerer mode after the initial data load already completed in procurer
     * mode), while still forcing a reload when the active company changes —
     * `_procurement` and offer-store data are both scoped to whichever
     * company was active at fetch time, so a stale company's data must never
     * be left showing after a switch.
     */
    _makeOfferViewLoadedForCompanyId?: string

    /**
     * Captures the procurement-level notification audit cutoff that was current
     * when offerer content last finished loading successfully, plus the company
     * and procurement identity it was loaded for (staleness anchors).
     * `partClosureAuditCutoff` must be captured at load time because
     * `_procurementDetailsNotificationSnapshot` advances independently and the
     * mark-as-seen call must use the cutoff that matches what was rendered.
     * Non-undefined iff offerer content is loaded and current.
    */
    _offererContentLoadNotificationContext?: {
        companyId?: string
        procurementId: string
        partClosureAuditCutoff?: string
    }
    _offerResultAcknowledgementKey?: string
    _offerPartClosureAcknowledgementKey?: string
    _offererContentReloadPromise?: Promise<boolean>
    _offererContentReloadRequested = NO
    _offererContentLoadGeneration = 0

    /**
     * Server cutoff captured before the procurement details snapshot was read.
     * Whole-procurement closure acknowledgement is allowed only against this
     * versioned content, never merely because a notification count exists.
     */
    _procurementDetailsNotificationSnapshot?: {
        companyId?: string
        procurementId: string
        auditCutoff: string
        loadedTabs: Set<ProcurementTab>
    }
    _procurementClosureAcknowledgementKey?: string

    /**
     * The procurement ID that was used for the most recent successful data load.
     * Used by handleRoute to detect navigation to a different procurement so that
     * stale lazy views are torn down and data is reloaded.
     */
    _contentProcurementId?: string
    _procurementDataLoadGeneration = 0

    private _openingProtocolExportsByProcurementId: Record<string, RHProcurementExcelExport> = {}
    private _openingProtocolExportLoadingProcurementIds = new Set<string>()
    _openingProtocolOfferCount = 0
    _openingProtocolSharing?: RHProcurementOpeningProtocolSharing
    _openingProtocolSharingProcurementIds = new Set<string>()
    
    // Lazily instantiated tab content views — never access these fields directly;
    // always go through the corresponding _load* method.
    _detailsView?: RHProcurementDetailsView
    private _makeOfferView?: MakeOfferView
    private _offersView?: RHOffersEvaluationView
    private _qaView?: RHQAView
    private _contractsView?: RHContractsView
    _teamView?: ProcurementTeamView
    _procurementTeam?: RHProcurementTeam
    _isApplyingProcurementTeamChange = NO
    _isAddingCurrentUserToProcurementTeam = NO
    
    
    override _focusManager = new RHFocusManager(() => {
        const items: (UIView | RHFocusManager)[] = [this.breadcrumbView]
        // Always include chrome buttons
        if (this.backButton.superview === this.view) {
            items.push(this.backButton)
        }
        if (this.registerAsTendererButton.superview === this.view) {
            items.push(this.registerAsTendererButton)
        }
        if (this.unaffiliatedButton.superview === this.view) {
            items.push(this.unaffiliatedButton)
        }
        if (this.loginButton.superview === this.view) {
            items.push(this.loginButton)
        }
        if (this.conflictConfirmationButton.superview === this.view) {
            items.push(this.conflictConfirmationButton)
        }
        if (this.askQuestionButton.superview === this.view) {
            items.push(this.askQuestionButton)
        }
        if (this.sendMessageButton.superview === this.view) {
            items.push(this.sendMessageButton)
        }
        if (
            this._currentTab === "details" &&
            this._detailsView &&
            this._detailsView?.tendererEngagementDetailsButton.superview === this._detailsView
        ) {
            items.push(this._detailsView.tendererEngagementDetailsButton)
        }
        if (
            this._currentTab === "details" &&
            this._detailsView?.showsOpeningProtocolExport &&
            !this._detailsView.showsOpeningProtocolUnavailableExplanation
        ) {
            items.push(this._detailsView.openingProtocolExportButtonView.button)
            if (!this._detailsView.openingProtocolShareButton.hidden) {
                items.push(this._detailsView.openingProtocolShareButton)
            }
        }
        if (this._currentTab === "details" && this._detailsView?.superview === this.view &&
            !this._detailsView.tendererRegistrationWithdrawalButton.hidden) {
            items.push(this._detailsView.tendererRegistrationWithdrawalButton)
        }
        // Tab buttons
        for (const tab of [
            this.detailsTabButton,
            this.teamTabButton,
            this.makeOfferTabButton,
            this.offersTabButton,
            this.qaTabButton,
            this.contractsTabButton
        ]) {
            if (this.tabBar.superview === this.view && !tab.hidden) {
                items.push(tab)
            }
        }
        // Active content pane
        const currentTab = this._currentTab
        if (this._usesRoundWorkspace && this._roundWorkspace?._focusManager) {
            items.push(this._roundWorkspace._focusManager)
        }
        else if (currentTab === "makeOffer" && this._makeOfferView?._focusManager) {
            items.push(this._makeOfferView._focusManager)
        }
        else if (currentTab === "team") {
            if (this._isInOffererMode && this._makeOfferView?.teamContentView?._focusManager) {
                items.push(this._makeOfferView.teamContentView._focusManager)
            }
            else if (this._teamView?._focusManager) {
                items.push(this._teamView._focusManager)
            }
        }
        else if (currentTab === "offers" && this._offersView?._focusManager) {
            items.push(this._offersView._focusManager)
        }
        else if (currentTab === "qa" && this._qaView?._focusManager) {
            items.push(this._qaView._focusManager)
        }
        return items
    })
    
    constructor(view: UIView) {
        super(view)

        this.view.configureWithObject({
            backgroundColor: RHColor.background
        })
        for (const button of [this.registerAsTendererButton, this.makeOfferTabButton]) {
            button.configureWithObject({
                updateContentForCurrentEnabledState: () => {
                    RHButton.prototype.updateContentForCurrentEnabledState.call(button)
                    if (button.enabled && this._shouldShowRegisterAsTendererButton && this._isOfferSubmissionClosed()) {
                        button.configureWithObject({ alpha: 0.5 })
                    }
                }
            })
        }

    }
    
    
    static override readonly routeComponentName = "procurement_details"
    static override readonly ParameterIdentifierName = {
        id: "id",
        part: "part",
        round: "round",
        tab: "tab",
        questionId: "questionId",
        qaSubtab: "qaSubtab",
        privateMessageCompanyId: "privateMessageCompanyId",
        backTo: "backTo",
        returnRoute: "returnRoute"
    }
    
    
    // ─── Lazy view factories ──────────────────────────────────────────────────
    
    /**
     * Returns the details view, creating it on first call.
     */
    _loadDetailsView(): RHProcurementDetailsView {
        if (!this._detailsView) {
            this._detailsView = new RHProcurementDetailsView()
            this._detailsView.userDidRequestProcurementModification = () => {
                this._routeToPublishedAmendmentEditor()
            }
            this._detailsView.userDidRequestProcurementClosureWithoutContract = () => {
                this._confirmProcurementClosureWithoutContract()
            }
            this._detailsView.userDidRequestTendererRegistrationWithdrawal = () => {
                const procurementId = this._procurement?._id?.toString()
                if (procurementId && this._detailsView) {
                    this._confirmWithdrawalOfTendererRegistrationForProcurement(
                        procurementId,
                        this._detailsView.tendererRegistrationWithdrawalButton
                    )
                }
            }
            this._detailsView.userDidRequestPartClosureWithoutContract = (partId: string) => {
                this._confirmProcurementPartClosureWithoutContract(partId)
            }
            this._detailsView.userDidRequestShareEmail = (recipients: string[], link: string) =>
                this._sendProcurementShareEmailToRecipientsWithLink(recipients, link)
            this._detailsView.userDidRequestCompanyInvitations = () => { void this._openCompanyInvitationDialog() }
            this.view.setNeedsLayoutUpToRootView()
        }
        return this._detailsView
    }
    
    /**
     * Mutation actions (cancel, deadline change, ...) get back the private
     * RHProcurement shape their socket action operates on, not the
     * viewer-scoped RHProcurementPublic this controller otherwise holds —
     * RHProcurement has no isCurrentUserCompanyRegisteredAsTenderer at all.
     * Overlaying the update onto the current public procurement keeps that
     * field (and any other public-only field) intact instead of losing it.
     */
    _procurementPublicByMergingUpdate(update: RHProcurement): RHProcurementPublic {
        return { ...this._procurement, ...update } as RHProcurementPublic
    }

    /**
     * Loads the full round chain (original hange plus every täpsustav hange
     * round after it, oldest first) this procurement belongs to, then rebuilds
     * the switcher. The server owns visibility and returns an access-filtered
     * chain; the client cannot infer chain membership from the generic public
     * DTO because directed child IDs intentionally stay private.
     */
    async _loadReconciliationRoundChain(
        procurementId: string,
        isLoadCurrent: () => boolean
    ): Promise<RHReconciliationRoundSummary[] | undefined> {
        let reconciliationRoundChain: RHReconciliationRoundSummary[] = []
        try {
            if (RHCore.sharedInstance.userProfile) {
                reconciliationRoundChain =
                    await RHCore.sharedInstance.procurementStore.retrieveReconciliationRoundChain(procurementId)
            }
        }
        catch (error) {
            if (isLoadCurrent()) {
                console.error("[ProcurementDetailsViewController] Failed to load reconciliation round chain:", error)
            }
        }

        if (!isLoadCurrent()) {
            return undefined
        }

        this._reconciliationRoundChain = reconciliationRoundChain
        return reconciliationRoundChain
    }

    _procurementDataForDetailsView(
        procurement: RHProcurementPublic
    ): RHProcurementPublic & { engagementSummary?: RHProcurementEngagementSummary } {
        const procurementData = procurement as RHProcurementPublic & {
            engagementSummary?: RHProcurementEngagementSummary
        }
        const engagementSummary = this._procurementEngagementSummary ?? procurementData.engagementSummary
        if (engagementSummary) {
            this._procurementEngagementSummary = engagementSummary
            procurementData.engagementSummary = engagementSummary
        }
        return procurementData
    }
    
    _loadTeamView(): ProcurementTeamView {
        if (!this._teamView) {
            this._teamView = new ProcurementTeamView()
            this._teamView.addsCurrentUserWhenMissing = NO
            this._teamView.showsHistory = YES
            this._teamView.showsConflictConfirmationStatus = YES
            this._teamView.teamDidChange = () => {
                this._applyProcurementTeamChange().catch(
                    error => console.error("Failed to save procurement team change", error)
                )
            }
            this._teamView.currentUserSelfAdditionWasRequested = role => {
                this._presentCurrentUserTeamSelfAdditionConfirmationForRole(role)
            }
            this.view.setNeedsLayoutUpToRootView()
        }
        return this._teamView
    }
    
    async _loadProcurementTeamView(procurementId: string) {
        const teamView = this._loadTeamView()
        teamView.addsCurrentUserWhenMissing = NO
        teamView.companyMemberInvitationTarget =
            this._routeForCompanyMemberInvitation(procurementId).linkRepresentation
        teamView.loading = YES
        
        try {
            const team = await RHCore.sharedInstance.procurementStore.retrieveProcurementTeam(procurementId)
            this._procurementTeam = team
            teamView.canEditResponsibleRole = team.capabilities?.canChangeResponsibleUser === YES
            teamView.canEditMemberRoles = team.capabilities?.canManageTeam === YES
            teamView.canCurrentUserAddSelfToTeam = team.capabilities?.canAddSelfToTeam === YES
            const procuringCompanyId = referenceId(
                this._procurement?.procuringCompany
            )
            await teamView.loadCompanyUsers(procuringCompanyId)
            teamView.configureFromProcurement(team)
        }
        finally {
            teamView.loading = NO
        }
    }


    _routeForCompanyMemberInvitation(procurementId: string): UIRoute {
        const companyParameters: Record<string, string> = {
            [CompanyViewController.ParameterIdentifierName.tab]: "administration",
            action: "inviteMember",
            sourceProcurementId: procurementId
        }
        const referenceNumber = this._procurement?.referenceNumber?.toString().trim()
        if (referenceNumber) {
            companyParameters.sourceProcurementReferenceNumber = referenceNumber
        }
        const backToComponentName = UIRoute.currentRoute
            .componentWithViewController(ProcurementDetailsViewController)
            ?.parameters[ProcurementDetailsViewController.ParameterIdentifierName.backTo]
        if (backToComponentName) {
            companyParameters.sourceProcurementBackTo = backToComponentName
        }

        return UIRoute.currentRoute
            .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
            .routeBySettingParameterInComponent("settings", "mode", "procurer")
            .routeWithComponent(
                SettingsViewController.routeComponentName,
                { [SettingsViewController.ParameterIdentifierName.tab]: "company" }
            )
            .routeWithComponent(CompanyViewController.routeComponentName, companyParameters)
    }

    _presentCurrentUserTeamSelfAdditionConfirmationForRole(role: RHTeamSelfAdditionRole) {
        const shower = CBDialogViewShower.showQuestionDialog(
            "Kinnitan huvide konflikti puudumise"
        )
        shower.dialogView.view.yesButton.titleLabel.text = "Kinnitan"
        shower.dialogView.view.noButton.titleLabel.text = "Tühista"
        shower.dialogView.view.setNeedsLayoutUpToRootView()

        shower.yesButtonWasPressed = async () => {
            shower.dialogView.dismiss()
            await this._addCurrentUserToProcurementTeam(role)
        }
    }

    async _addCurrentUserToProcurementTeam(role: RHTeamSelfAdditionRole) {
        if (this._isAddingCurrentUserToProcurementTeam) {
            return
        }
        const procurementId = this._procurement?._id?.toString()
        if (!procurementId || !this._teamView) {
            return
        }

        const teamView = this._teamView
        let wasCurrentUserAdded = NO
        try {
            this._isAddingCurrentUserToProcurementTeam = YES
            teamView.loading = YES
            await RHCore.sharedInstance.procurementStore.addCurrentUserToProcurementTeamForRole(
                procurementId,
                role
            )
            wasCurrentUserAdded = YES
            await RHCore.sharedInstance.procurementStore.confirmProcurementConflictOfInterest(
                procurementId
            )
            await this._loadProcurementData()
        }
        catch (error) {
            console.error("Procurement team self-addition error:", error)
            await this._loadProcurementData()
            if (wasCurrentUserAdded) {
                CBDialogViewShower.alert(
                    "Sind lisati hankemeeskonda, kuid huvide konflikti kinnitamine ebaõnnestus. " +
                    "Palun kinnita huvide konflikti puudumine eraldi."
                )
            }
            else {
                let errorMessage = "Hankemeeskonnaga liitumine ebaõnnestus."
                if (error instanceof Error && error.message) {
                    errorMessage = error.message
                }
                CBDialogViewShower.alert(errorMessage)
            }
        }
        finally {
            teamView.loading = NO
            this._isAddingCurrentUserToProcurementTeam = NO
            this.view.setNeedsLayoutUpToRootView()
        }
    }
    
    async _applyProcurementTeamChange() {
        if (this._isApplyingProcurementTeamChange) {
            return
        }
        if (!this._teamView || !this._procurementTeam || !this._procurement?._id) {
            return
        }
        
        const teamView = this._teamView
        const validationMessage = teamView.validationMessage()
        if (validationMessage) {
            CBDialogViewShower.alert(validationMessage)
            teamView.configureFromProcurement(this._procurementTeam)
            return
        }
        
        const procurementId = this._procurement._id + ""
        const previousResponsibleUserId = RHTeamUserReferenceResolver.userIdFromReference(
            this._procurementTeam.responsibleProcuringUser
        )
        const previousTeamMemberIds = this._procurementTeamMemberIds(this._procurementTeam)
        
        try {
            this._isApplyingProcurementTeamChange = YES
            teamView.loading = YES
            
            let team = await RHCore.sharedInstance.procurementStore.updateProcurementTeam(
                procurementId,
                {
                    authorizedProcuringUserIds: teamView.authorizedUserIds,
                    observerProcuringUserIds: teamView.observerUserIds,
                    evaluatorProcuringUserIds: teamView.evaluatorUserIds
                }
            )
            if (teamView.responsibleUserId !== previousResponsibleUserId) {
                team = await RHCore.sharedInstance.procurementStore.changeProcurementResponsibleUser(
                    procurementId,
                    teamView.responsibleUserId
                )
            }
            this._procurementTeam = team
            this._procurementCapabilities = team.capabilities ?? this._procurementCapabilities
            teamView.canEditResponsibleRole = team.capabilities?.canChangeResponsibleUser === YES
            teamView.canEditMemberRoles = team.capabilities?.canManageTeam === YES
            teamView.configureFromProcurement(team)
            await this._loadProcurementData()
            const newlyAddedConflictedUserIds = (team.userIdsWithActiveOfferConflicts ?? [])
                .filter(userId => !previousTeamMemberIds.has(userId))
            if (newlyAddedConflictedUserIds.length > 0) {
                CBDialogViewShower.alert(
                    "Hoiatus: hankemeeskonda lisatud kasutaja osaleb sama kasutajakontoga sellel hankel ka pakkuja poolel. " +
                    "Muudatus salvestati; hoiatus ei keela kasutajal hankemeeskonnas osaleda."
                )
            }
        }
        catch (error) {
            console.error("Procurement team update error:", error)
            let errorMessage = "Meeskonna uuendamine ebaõnnestus."
            if (error instanceof Error && error.message) {
                errorMessage = error.message
            }
            CBDialogViewShower.alert(errorMessage)
            if (this._procurementTeam) {
                teamView.configureFromProcurement(this._procurementTeam)
            }
        }
        finally {
            teamView.loading = NO
            this._isApplyingProcurementTeamChange = NO
            this.view.setNeedsLayoutUpToRootView()
        }
    }


    _procurementTeamMemberIds(team: RHProcurementTeam): Set<string> {
        const userIds = new Set<string>()
        for (const reference of [
            team.responsibleProcuringUser,
            ...(team.authorizedProcuringUsers ?? []),
            ...(team.observerProcuringUsers ?? []),
            ...(team.evaluatorProcuringUsers ?? [])
        ]) {
            const userId = RHTeamUserReferenceResolver.userIdFromReference(reference)
            if (userId) {
                userIds.add(userId)
            }
        }
        return userIds
    }
    
    /**
     * Returns the make-offer view, creating and wiring it on first call.
     * Only call this when the current user is eligible to submit offers.
     */
    private _loadMakeOfferView(): MakeOfferView {
        if (!this._makeOfferView) {
            this._makeOfferView = new MakeOfferView()
            this._makeOfferView.offerStateDidChange = (state, offer, capabilities) => {
                this._syncUserOffersWithMakeOfferState(state, offer)
                this._makeOfferTabState = state
                this._offerCapabilities = capabilities
                this._updateMakeOfferTabButtonTitle()
                this._updateTabButtonBadges()
                this.view.setNeedsLayout()
            }
            this._makeOfferView.didSubmitProcurementOffer = (offer: RHProcurementOffer) => {
                console.log("Offer submitted:", offer)
                this.view.setNeedsLayout()
            }
            this._makeOfferView.didSaveDraftAndNavigateToDashboard = () => {
                UIRoute.currentRoute
                    .routeByRemovingComponentNamed(ProcurementDetailsViewController.routeComponentName)
                    .routeWithComponent(UserMainViewController.routeComponentName, {
                        [UserMainViewController.ParameterIdentifierName.offererFilter]: "draftOffer"
                    })
                    .applyWithoutRouteChangeApproval()
            }
        }
        this._makeOfferView.offerActionsEnabled = !this._isOfferSubmissionClosed()
        return this._makeOfferView
    }
    
    /**
     * Returns the incoming-offers evaluation view, creating it on first call.
     * Only call this when the current user is the procurement owner.
     */
    private _loadOffersView(): RHOffersEvaluationView {
        if (!this._offersView) {
            this._offersView = new RHOffersEvaluationView()
            this._offersView.procurementCapabilities = this._procurementCapabilities
            this._offersView.offersWereOpened = () => {
                this._updateOpeningProtocolExportRow()
                this.view.setNeedsLayoutUpToRootView()
            }
            this._offersView.partEvaluationDidFinalize = () => {
                this._handlePartEvaluationDidFinalize()
            }
            this._offersView.evaluationContentDidLoad = (procurementId, snapshot) => {
                this._markSubmittedOfferNotificationsAsSeenForProcurementWithSnapshot(procurementId, snapshot)
            }
            this._offersView.createReconciliationDidRequest = (procurementId, partId) => {
                this._showCreateReconciliationDialog(procurementId, partId)
            }
            this._offersView.cancelPartDidRequest = (procurementId, partId) => {
                this._confirmProcurementPartClosureWithoutContract(partId)
            }
        }
        return this._offersView
    }


    _reconciliationDialog?: CreateReconciliationProcurementDialogView

    /** "Loo täpsustav hange" on a tied-price part — shows the create-and-publish dialog directly, no editor. */
    _showCreateReconciliationDialog(procurementId: string, partId: string) {
        const source = this._roundContext.informationByProcurementId.get(procurementId)?.procurement ?? this._procurement
        const part = source?.parts.find(candidatePart => candidatePart._id.toString() === partId)
        if (!this._reconciliationDialog) {
            this._reconciliationDialog = new CreateReconciliationProcurementDialogView()
        }
        void this._reconciliationDialog.showForPart(
            procurementId,
            partId,
            part?.partNumber ?? 1,
            childProcurementId => {
                const rootPartId = [...this._roundContext.roundsByPartId.values()].flat().find(round =>
                    round.procurementId === procurementId && round.partId === partId
                )?.rootPartId ?? partId
                UIRoute.currentRoute
                    .routeBySettingParameterInComponent(ProcurementDetailsViewController.routeComponentName, "round", childProcurementId)
                    .routeBySettingParameterInComponent(ProcurementDetailsViewController.routeComponentName, "part", rootPartId)
                    .apply()
                void this._loadProcurementData()
            }
        )
    }


    get _usesRoundWorkspace(): boolean {
        if (!this._roundContext.hasRounds || this._showsProcurementUnavailableState) {
            return NO
        }
        return this._currentTab === "details" ||
            (this._currentTab === "offers" && this._shouldShowIncomingOffersPanel) ||
            (this._currentTab === "makeOffer" && this._shouldShowMakeOfferPanel)
    }

    _configureRoundWorkspaceForCurrentRoute() {
        if (!this._roundContext.hasRounds) {
            this._roundWorkspace?.removeFromSuperview()
            return
        }
        const parameters = UIRoute.currentRoute.componentWithViewController(
            ProcurementDetailsViewController
        )?.parameters
        if (!parameters?.round) {
            this._roundContext.selectedProcurementIdsByPartId.clear()
            this._roundContext.focusedPartId = undefined
        }
        if (parameters?.round) {
            for (const [partId, rounds] of this._roundContext.roundsByPartId) {
                if (parameters.part && parameters.part !== partId) {
                    continue
                }
                const round = rounds.find(candidate => candidate.procurementId === parameters.round)
                if (round) {
                    this._roundContext.selectRoundForPart(round.procurementId, partId)
                    break
                }
            }
        }
        if (!this._roundWorkspace) {
            this._roundWorkspace = new ProcurementRoundWorkspaceView().configuredWithObject({
                roundSelectionDidRequest: async (round: ProcurementRound) => {
                    if (!await this.navigationWillReset()) {
                        return
                    }
                    UIRoute.currentRoute
                        .routeBySettingParameterInComponent(ProcurementDetailsViewController.routeComponentName, "part", round.rootPartId)
                        .routeBySettingParameterInComponent(ProcurementDetailsViewController.routeComponentName, "round", round.procurementId)
                        .apply()
                },
                amendmentDidRequest: (procurementId: string) => this._routeToPublishedAmendmentEditor(procurementId),
                closureDidRequest: (round: ProcurementRound) => this._confirmRoundClosure(round),
                userDidRequestTendererRegistrationWithdrawal: (procurementId: string, button: RHButton) => {
                    this._confirmWithdrawalOfTendererRegistrationForProcurement(procurementId, button)
                },
                reconciliationDidRequest: (procurementId: string, partId: string) => this._showCreateReconciliationDialog(procurementId, partId),
                procurementDidChange: () => { void this._loadProcurementData() },
                offererContentDidLoad: (round: ProcurementRound, snapshot: RHMakeOfferLoadResult) => {
                    this._markRoundOffererNotificationsAsSeen(round, snapshot)
                },
                offerContentDidLoad: (procurementId: string, snapshot: RHOfferNotificationSnapshot) => {
                    this._markSubmittedOfferNotificationsAsSeenForProcurementWithSnapshot(procurementId, snapshot)
                },
                roundDetailsViewDidConfigure: (
                    detailsView: RHProcurementDetailsView,
                    round: ProcurementRound
                ) => {
                    this._configureOpeningProtocolExportInDetailsViewForInformation(
                        detailsView,
                        round.information
                    )
                }
            })
        }
        if (this._usesRoundWorkspace) {
            this._roundWorkspace.configureForContext(this._roundContext, this._currentTab as ProcurementRoundTab)
        }
    }

    _markRoundOffererNotificationsAsSeen(round: ProcurementRound, snapshot: RHMakeOfferLoadResult) {
        if (!this._isInOffererMode || this._currentTab !== "makeOffer" ||
            this._roundWorkspace?.superview !== this.view ||
            this._roundContext.selectedRoundForPart(round.rootPartId) !== round ||
            snapshot.offerAccessDate === null) {
            return
        }
        const information = round.information
        const remainingCount = information.unseenQANotificationCount + information.unseenProcurementNotificationCount
        void RHCore.sharedInstance.notificationStore.markSeen(
            round.procurementId, ["offer.opened", "offer.notification_sent", "offer.required_documents_changed"], remainingCount,
            undefined, undefined, snapshot.offerAccessDate
        ).catch(error => console.error("Failed to mark round offer notifications as seen", error))
        const closedPartIds = information.procurement.closedWithoutContractPartIds ?? []
        const allClosuresVisible = closedPartIds.length > 0 && closedPartIds.every(partId =>
            [...this._roundContext.roundsByPartId.keys()].some(rootPartId => {
                const selected = this._roundContext.selectedRoundForPart(rootPartId)
                return selected?.procurementId === round.procurementId && selected.partId === partId
            })
        )
        if (allClosuresVisible && snapshot.renderedClosedWithoutContractPartIds.length > 0 &&
            information.notificationSnapshotAt && !Number.isNaN(new Date(information.notificationSnapshotAt).getTime())) {
            void RHCore.sharedInstance.notificationStore.markSeen(
                round.procurementId, ["offer.part_closed_without_contract"], remainingCount,
                undefined, undefined, information.notificationSnapshotAt
            ).catch(error => console.error("Failed to mark round closure notifications as seen", error))
        }
        const activeCompanyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        requestAnimationFrame(() => {
            if (!this.view.isMemberOfViewTree || this._currentTab !== "makeOffer" ||
                this._roundWorkspace?.superview !== this.view ||
                this._roundContext.selectedRoundForPart(round.rootPartId) !== round ||
                RHCore.sharedInstance.companyAffiliationStore.activeCompanyId !== activeCompanyId) return
            const notificationStore = RHCore.sharedInstance.notificationStore
            notificationStore.markPendingNotificationAsReadIfContentMatches({ procurementId: round.procurementId, tab: "makeOffer",
                eventTypes: ["offer.opened", "offer.notification_sent", "offer.required_documents_changed",
                    "offer.evaluation_reopened", "procurement.reconciliation_invitation_sent"],
                auditCutoffDate: snapshot.offerAccessDate ?? undefined })
            if (allClosuresVisible && snapshot.renderedClosedWithoutContractPartIds.length > 0 && information.notificationSnapshotAt) {
                notificationStore.markPendingNotificationAsReadIfContentMatches({ procurementId: round.procurementId, tab: "makeOffer",
                    eventTypes: ["offer.part_closed_without_contract"], auditCutoffDate: information.notificationSnapshotAt })
            }
        })
        if (closedPartIds.length === 0 || allClosuresVisible) {
            information.unseenOfferNotificationCount = 0
            if (round.procurementId === this._procurement?._id?.toString()) {
                this._unseenOfferNotificationCount = 0
            }
        }
        this._updateTabButtonBadges()
    }

    _confirmRoundClosure(round: ProcurementRound) {
        if (this._roundContext.isHistoricalRound(round) || round.information.capabilities?.canFinalizeOrCancel !== YES) {
            return
        }
        const procurement = round.information.procurement
        const part = procurement.parts.find(candidate => candidate._id.toString() === round.partId)
        if (!part) {
            return
        }
        const rootProcurement = this._roundContext.rootInformation!.procurement
        const rootPart = rootProcurement.parts.find(candidate => candidate._id.toString() === round.rootPartId)!
        if (rootProcurement.parts.length > 1) {
            this.closureDialog.configureForProcurementPartClosure(rootPart.partNumber)
        }
        else {
            this.closureDialog.configureForProcurementClosure()
        }
        this.closureDialog.userDidConfirmClosure = async (reason: string) => {
            this.closureDialog.dismiss()
            try {
                if (procurement.parts.length > 1) {
                    await RHCore.sharedInstance.procurementStore.closeProcurementPartWithoutContract(
                        round.procurementId, round.partId, reason
                    )
                }
                else {
                    await RHCore.sharedInstance.procurementStore.closeProcurementWithoutContract(round.procurementId, reason)
                }
                await this._loadProcurementData()
            }
            catch (error) {
                console.error("Round closure failed:", error)
                CBDialogViewShower.alert("Lõpetamine ilma lepinguta ebaõnnestus.")
            }
        }
        this.closureDialog.showInRootView(YES)
    }

    _handlePartEvaluationDidFinalize() {
        const procurementId = this._procurement?._id?.toString()
        if (procurementId && this._shouldShowContractsTab) {
            RHCore.sharedInstance.contractStore.invalidateContractsForProcurement(procurementId)
        }
        this._updateContentForCurrentMode()
        this.view.setNeedsLayoutUpToRootView()
    }
    
    /**
     * Returns the Q&A view, creating it on first call.
     * Only call this when the current user is allowed to see Q&A.
     */
    _loadQAView(): RHQAView {
        if (!this._qaView) {
            this._qaView = new RHQAView().configuredWithObject({
                backgroundColor: UIColor.whiteColor,
                numberOfCommunicationEntriesDidChange: (numberOfCommunicationEntries: number) => {
                    this.qaTabButton.titleLabel.text = `Teabevahetus (${numberOfCommunicationEntries})`
                }
            })
        }
        return this._qaView
    }
    
    /**
     * Returns the contracts view, creating it on first call.
     * Shown once at least one winning part has been finalized and the current
     * user is either the procurer/auditor or a registered offerer.
     */
    private _loadContractsView(): RHContractsView {
        if (!this._contractsView) {
            this._contractsView = new RHContractsView()
        }
        this._contractsView.roundContext = this._roundContext
        return this._contractsView
    }
    
    
    // ─── Badge management ─────────────────────────────────────────────────────
    
    /**
     * Applies the current unseen counts to the relevant tab button badges.
     * Call after loading procurement data and after marking notifications as seen.
     */
    private _updateTabButtonBadges() {
        let roundQAUnread = 0
        let roundOfferUnread = 0
        let roundQAAttention = NO
        let roundOfferAttention = NO
        for (const [sourceId, information] of this._roundContext.informationByProcurementId) {
            if (sourceId === this._procurement?._id?.toString()) {
                continue
            }
            roundQAUnread += information.unseenQANotificationCount + information.unseenProcurementNotificationCount
            roundOfferUnread += information.unseenOfferNotificationCount
            roundQAAttention ||= information.requiresProcurerQAAttention === YES
            roundOfferAttention ||= information.requiresProcurerAttention === YES
        }
        RHNotificationBadge.applyUnreadEmphasis(
            this.qaTabButton.titleLabel,
            this._unseenQANotificationCount + this._unseenProcurementNotificationCount + roundQAUnread > 0,
            "Lugemata teabevahetuse teavitus."
        )
        RHNotificationBadge.applyDot(
            this.qaTabButton.titleLabel,
            this._requiresProcurerQAAttention || roundQAAttention,
            "Vastamata küsimus ootab vastust."
        )

        let offersTabUnread = this._unseenOfferNotificationCount + roundOfferUnread > 0
        let offersTabNeedsAction = this._requiresProcurerAttention || roundOfferAttention
        if (this._isInOffererMode) {
            offersTabUnread = NO
            offersTabNeedsAction = NO
        }
        RHNotificationBadge.applyUnreadEmphasis(
            this.offersTabButton.titleLabel,
            offersTabUnread,
            "Lugemata pakkumusega seotud teavitus."
        )
        RHNotificationBadge.applyDot(
            this.offersTabButton.titleLabel,
            offersTabNeedsAction,
            "Pakkumused ootavad hindamist."
        )

        const hasUnreadOfferMessage = this._userOffers.some(offer =>
            RHCore.sharedInstance.notificationStore.offerMessageCount(offer._id + "") > 0
        )
        let makeOfferShow = hasUnreadOfferMessage
        let makeOfferTooltip = "Lugemata sõnum pakkumuse vestluses."
        if (this._isInOffererMode && this._unseenOfferNotificationCount + roundOfferUnread > 0) {
            makeOfferShow = YES
            makeOfferTooltip = "Pakkumusega seotud teavitus."
        }
        RHNotificationBadge.applyUnreadEmphasis(
            this.makeOfferTabButton.titleLabel,
            makeOfferShow,
            makeOfferTooltip
        )
        let isOfferResubmissionRequired = this._makeOfferView?.isOfferResubmissionRequired === YES
        if (this._roundContext.hasRounds) {
            isOfferResubmissionRequired = this._roundWorkspace?.isOfferResubmissionRequired === YES
        }
        if (this._isInOffererMode && isOfferResubmissionRequired) {
            RHNotificationBadge.applyDot(
                this.makeOfferTabButton.titleLabel,
                YES,
                "Kohustuslikud dokumendid puuduvad. Esita pakkumus uuesti."
            )
        }
    }
    
    
    // Item counts and notification markers are independent.
    _updateTabButtonCounts(offerCount: number) {
        // Teabevahetus includes the visible public threads and private messages.
        if (this._canSeeQA) {
            this.qaTabButton.titleLabel.text = `Teabevahetus (${this._loadQAView().numberOfCommunicationEntries})`
        }
        
        // Pakkumused — submitted offers for this procurement (procurer only).
        if (this._shouldShowIncomingOffersPanel) {
            this.offersTabButton.titleLabel.text = `Pakkumused (${offerCount})`
        }
    }
    
    
    private _syncMakeOfferTabStateFromUserOffers() {
        const submittedOffer = this._userOffers.find(userOffer => userOffer.status === "submitted")
        if (submittedOffer) {
            this._makeOfferTabState = "submitted"
            this._updateMakeOfferTabButtonTitle()
            return
        }
        
        const draftOffer = this._userOffers.find(userOffer => userOffer.status === "draft")
        if (draftOffer) {
            this._makeOfferTabState = "draft"
            this._updateMakeOfferTabButtonTitle()
            return
        }
        
        this._makeOfferTabState = "empty"
        // The server retains rescinded offers only when a procurer closed an offered part.
        if (this._userOffers.some(userOffer => userOffer.status === "rescinded")) {
            this._makeOfferTabState = "submitted"
        }
        this._updateMakeOfferTabButtonTitle()
    }
    
    
    private _syncUserOffersWithMakeOfferState(
        state: RHProcurementOfferTabState,
        offer?: RHProcurementOffer
    ) {
        this._userOffers = this._userOffers.filter(userOffer =>
            userOffer.status !== "draft" && userOffer.status !== "submitted"
        )
        
        if (state === "empty") {
            return
        }
        
        if (offer) {
            this._userOffers.push(offer)
        }
    }
    
    
    private _updateMakeOfferTabButtonTitle() {
        let tabTitle = ProcurementDetailsViewController._procurementOfferActionTitle(
            this._isRegisteredAsTenderer,
            this._makeOfferTabState,
            this._offerCapabilities?.canViewDetailed !== NO
        )

        if (this._isViewingProcurementClosedWithoutContractAsOfferer && this._shouldShowMakeOfferPanel) {
            tabTitle = "Minu pakkumus"
        }

        const selectedCurrentReconciliationRounds: ProcurementRound[] = []
        for (const partId of this._roundContext.roundsByPartId.keys()) {
            if (this._roundContext.focusedPartId && this._roundContext.focusedPartId !== partId) {
                continue
            }
            const selectedRound = this._roundContext.selectedRoundForPart(partId)
            if (!selectedRound || selectedRound.roundNumber === 0 || this._roundContext.isHistoricalRound(selectedRound)) {
                continue
            }
            selectedCurrentReconciliationRounds.push(selectedRound)
        }
        if (selectedCurrentReconciliationRounds.length > 0) {
            tabTitle = "Esita pakkumus"
            if (selectedCurrentReconciliationRounds.every(round =>
                round.information.userOffers.some(offer => offer.status === "submitted")
            )) {
                tabTitle = "Minu pakkumus"
            }
        }

        if (this.makeOfferTabButton.titleLabel.text !== tabTitle) {
            this.makeOfferTabButton.titleLabel.text = tabTitle
        }
    }


    /**
     * `canViewOfferDetails` reflects the current user's own access to the
     * company's active offer (RHOfferCapabilities.canViewDetailed) — separate
     * from `offerTabState`, which only reflects whether the *company* has one at
     * all. A teammate-created draft the current user has no team role on must
     * not read as "Jätka koostamist" ("continue"), since opening the tab shows a
     * restricted-access banner, not an editable draft.
     */
    static _procurementOfferActionTitle(
        isRegisteredAsTenderer: boolean,
        offerTabState: RHProcurementOfferTabState,
        canViewOfferDetails: boolean
    ): string {
        if (!isRegisteredAsTenderer) {
            return "Registreeru hankele"
        }

        if (offerTabState === "submitted") {
            return "Minu pakkumus"
        }

        if (offerTabState === "draft") {
            if (!canViewOfferDetails) {
                return "Pakkumus"
            }
            return "Jätka koostamist"
        }

        return "Koosta pakkumus"
    }


    /**
     * Clears the QA unseen count locally, updates the tab badge, and tells the
     * notification store to mark the procurement's rendered information-exchange
     * notifications as seen on the server. The marking is type-filtered so reading the Q&A tab
     * never clears unseen offer.submitted notifications (and vice versa).
     * The store will notify all subscribers (including the main view controllers)
     * so their row badges also clear.
     */
    _markQANotificationsAsSeenForProcurement(procurementId: string) {
        this._markPendingNotificationAsReadForVisibleContent()
        const qaView = this._loadQAView()
        if (qaView.selectedSubtabKey !== "public") {
            return
        }
        for (const sourceId of qaView.communicationLabelsByProcurementId.keys()) {
            const snapshot = qaView.notificationSnapshotForProcurement(sourceId)
            if (snapshot.questionIds.length + snapshot.answeredQuestionIds.length + snapshot.broadcastIds.length +
                (snapshot.amendmentIds ?? []).length === 0) {
                continue
            }
            const information = this._roundContext.informationByProcurementId.get(sourceId)
            const remainingCount = information?.unseenOfferNotificationCount ?? this._unseenOfferNotificationCount
            RHCore.sharedInstance.notificationStore.markSeen(
                sourceId,
                ["question.asked", "question.answered", "procurerBroadcast.sent", "procurement.amended", "procurement.closed_without_contract"],
                remainingCount,
                snapshot
            ).then(() => {
                RHCore.sharedInstance.procurementStore.invalidatePublishedProcurements([sourceId])
            }).catch(error => console.error("Failed to mark QA notifications as seen", error))
            if (information) {
                information.unseenQANotificationCount = 0
                information.unseenProcurementNotificationCount = 0
            }
        }
        if (this._procurement?._id?.toString() === procurementId) {
            this._unseenQANotificationCount = 0
            this._unseenProcurementNotificationCount = 0
        }
        this._updateTabButtonBadges()
    }
    
    
    /**
     * Clears offer-related notifications only on the tab where that role can
     * act on them: procurers clear offer.submitted on "Pakkumused", offerers
     * clear offer.opened / offer.notification_sent / part-closure results on "Minu pakkumus".
     *
     * Owner acknowledgement additionally requires the exact incoming-offers
     * snapshot that RHOffersEvaluationView successfully applied while mounted.
     * It still happens only after the deadline has passed — before that the
     * deadline-gated badge has not been shown and marking would swallow it.
     */
    _markSubmittedOfferNotificationsAsSeenForProcurementWithSnapshot(
        procurementId: string,
        offerSnapshot: RHOfferNotificationSnapshot
    ) {
        if (this._currentTab !== "offers" || !this._isCurrentUserOnProcuringSide) {
            return
        }
        const currentProcurementId = UIRoute.currentRoute
            .componentWithViewController(ProcurementDetailsViewController)
            ?.parameters.id
        const visibleRound = [...this._roundContext.roundsByPartId.keys()].map(partId =>
            this._roundContext.selectedRoundForPart(partId)
        ).find(round => round?.procurementId === procurementId)
        const isVisibleRoundContent = this._usesRoundWorkspace &&
            this._roundWorkspace?.superview === this.view && !!visibleRound
        if ((!isVisibleRoundContent && (currentProcurementId !== procurementId ||
            this._offersView?.superview !== this.view))) {
            return
        }
        const deadline = visibleRound?.information.procurement.deadlineDate ?? this._procurement?.deadlineDate
        if (!deadline || new Date(deadline) > new Date()) {
            return
        }

        const activeCompanyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        requestAnimationFrame(() => {
            const selectedRound = visibleRound && this._roundContext.selectedRoundForPart(visibleRound.rootPartId)
            if (!this.view.isMemberOfViewTree || this._currentTab !== "offers" ||
                RHCore.sharedInstance.companyAffiliationStore.activeCompanyId !== activeCompanyId) return
            if (isVisibleRoundContent) {
                if (this._roundWorkspace?.superview !== this.view || selectedRound !== visibleRound) return
            } else if (this._contentProcurementId !== procurementId || !this._offersView?.isMemberOfViewTree) return
            RHCore.sharedInstance.notificationStore.markPendingNotificationAsReadIfContentMatches({
                procurementId, tab: "offers", eventTypes: ["offer.submitted", "offer.part_modified"], offerSnapshot
            })
            RHCore.sharedInstance.notificationStore.markPendingNotificationAsReadIfContentMatches({
                procurementId, tab: "offers",
                eventTypes: ["offer.rescinded", "offer.part_rescinded", "offer.evaluation_reopened"],
                auditCutoffDate: offerSnapshot.auditCutoff
            })
        })

        if (offerSnapshot.offerIds.length === 0) return
        if (currentProcurementId === procurementId) {
            this._unseenOfferNotificationCount = 0
        }
        const sourceInformation = visibleRound?.information
        if (sourceInformation) {
            sourceInformation.unseenOfferNotificationCount = 0
        }
        this._updateTabButtonBadges()
        const remainingCount = (sourceInformation?.unseenQANotificationCount ?? this._unseenQANotificationCount) +
            (sourceInformation?.unseenProcurementNotificationCount ?? this._unseenProcurementNotificationCount)
        RHCore.sharedInstance.notificationStore.markSeen(
            procurementId,
            ["offer.submitted", "offer.part_modified"],
            remainingCount,
            undefined,
            offerSnapshot
        ).catch(
            error => console.error("Failed to mark offer notifications as seen", error)
        )
    }


    async _loadOffererNotificationContent(
        procurement: RHProcurementPublic
    ): Promise<boolean> {
        const procurementId = procurement._id + ""
        const activeCompanyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        const procurementNotificationSnapshot = this._procurementDetailsNotificationSnapshot
        const loadGeneration = ++this._offererContentLoadGeneration
        this._offererContentLoadNotificationContext = undefined
        try {
            const makeOfferView = this._loadMakeOfferView()
            await makeOfferView.loadForProcurement(procurement, procurement.parts.length)
            if (
                loadGeneration !== this._offererContentLoadGeneration ||
                this._procurement?._id?.toString() !== procurementId ||
                RHCore.sharedInstance.companyAffiliationStore.activeCompanyId !== activeCompanyId
            ) {
                return NO
            }
            const contentLoadResult = makeOfferView.lastLoadResult
            if (!contentLoadResult || contentLoadResult.offerAccessDate === null) {
                return NO
            }
            const offerAuditCutoff = contentLoadResult.offerAccessDate

            let partClosureAuditCutoff: string | undefined
            const renderedClosedWithoutContractPartIds =
                contentLoadResult.renderedClosedWithoutContractPartIds
            if (
                renderedClosedWithoutContractPartIds.length > 0 &&
                procurementNotificationSnapshot?.procurementId === procurementId &&
                procurementNotificationSnapshot.companyId === activeCompanyId &&
                !Number.isNaN(new Date(procurementNotificationSnapshot.auditCutoff).getTime())
            ) {
                // Rendered procurement DTO state must use the matching details cutoff.
                partClosureAuditCutoff = procurementNotificationSnapshot.auditCutoff
            }
            this._offererContentLoadNotificationContext = {
                companyId: activeCompanyId,
                procurementId,
                partClosureAuditCutoff
            }
            return YES
        }
        catch (error) {
            if (loadGeneration === this._offererContentLoadGeneration) {
                this._offererContentLoadNotificationContext = undefined
            }
            console.error("Failed to load offerer notification content", error)
            return NO
        }
    }


    _invalidateOffererNotificationContent() {
        this._offererContentLoadNotificationContext = undefined
        this._makeOfferViewLoadedForCompanyId = undefined
        this._offererContentLoadGeneration += 1
    }


    async _reloadOffererNotificationContent(): Promise<boolean> {
        this._offererContentReloadRequested = YES
        if (this._offererContentReloadPromise) {
            return this._offererContentReloadPromise
        }
        const reloadPromise = (async () => {
            let didLoad = NO
            do {
                this._offererContentReloadRequested = NO
                const procurement = this._procurement
                if (!procurement) {
                    return NO
                }
                didLoad = await this._loadOffererNotificationContent(procurement)
            } while (this._offererContentReloadRequested)
            return didLoad
        })()
        this._offererContentReloadPromise = reloadPromise
        try {
            return await reloadPromise
        }
        finally {
            if (this._offererContentReloadPromise === reloadPromise) {
                this._offererContentReloadPromise = undefined
            }
        }
    }


    _markOffererNotificationsAsSeenForProcurementAfterContentLoad(
        procurementId: string,
        ignoreKnownCount: boolean = NO
    ) {
        this._markPendingNotificationAsReadForVisibleContent()
        if (this._currentTab !== "makeOffer" || !this._isInOffererMode || this._usesRoundWorkspace) {
            return
        }
        if (!ignoreKnownCount && this._unseenOfferNotificationCount === 0) {
            return
        }
        const currentProcurementId = UIRoute.currentRoute
            .componentWithViewController(ProcurementDetailsViewController)
            ?.parameters.id
        const activeCompanyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        const loadContext = this._offererContentLoadNotificationContext
        const lastLoadResult = this._loadMakeOfferView().lastLoadResult
        if (
            !loadContext ||
            !lastLoadResult ||
            lastLoadResult.offerAccessDate === null ||
            currentProcurementId !== procurementId ||
            loadContext.procurementId !== procurementId ||
            loadContext.companyId !== activeCompanyId
        ) {
            return
        }

        const offerAuditCutoff = lastLoadResult.offerAccessDate
        const offerResultAcknowledgementKey = [
            activeCompanyId ?? "",
            procurementId,
            offerAuditCutoff
        ].join(":")
        const shouldAcknowledgeOfferResult =
            this._offerResultAcknowledgementKey !== offerResultAcknowledgementKey

        const partClosureAuditCutoff = loadContext.partClosureAuditCutoff
        let partClosureAcknowledgementKey: string | undefined
        let shouldAcknowledgePartClosure = NO
        if (
            partClosureAuditCutoff &&
            lastLoadResult.renderedClosedWithoutContractPartIds.length > 0
        ) {
            const renderedPartIds = lastLoadResult.renderedClosedWithoutContractPartIds
                .slice()
                .sort()
                .join(",")
            partClosureAcknowledgementKey = [
                activeCompanyId ?? "",
                procurementId,
                partClosureAuditCutoff,
                renderedPartIds
            ].join(":")
            shouldAcknowledgePartClosure =
                this._offerPartClosureAcknowledgementKey !== partClosureAcknowledgementKey
        }

        if (!shouldAcknowledgeOfferResult && !shouldAcknowledgePartClosure) {
            return
        }

        this._unseenOfferNotificationCount = 0
        this._updateTabButtonBadges()
        const remainingCount =
            this._unseenQANotificationCount + this._unseenProcurementNotificationCount

        if (shouldAcknowledgeOfferResult) {
            this._offerResultAcknowledgementKey = offerResultAcknowledgementKey
            RHCore.sharedInstance.notificationStore.markSeen(
                procurementId,
                ["offer.opened", "offer.notification_sent", "offer.required_documents_changed"],
                remainingCount,
                undefined,
                undefined,
                offerAuditCutoff
            ).catch(error => {
                if (this._offerResultAcknowledgementKey === offerResultAcknowledgementKey) {
                    this._offerResultAcknowledgementKey = undefined
                }
                console.error("Failed to mark offerer result notifications as seen", error)
            })
        }

        if (
            shouldAcknowledgePartClosure &&
            partClosureAuditCutoff &&
            partClosureAcknowledgementKey
        ) {
            this._offerPartClosureAcknowledgementKey = partClosureAcknowledgementKey
            RHCore.sharedInstance.notificationStore.markSeen(
                procurementId,
                ["offer.part_closed_without_contract"],
                remainingCount,
                undefined,
                undefined,
                partClosureAuditCutoff
            ).catch(error => {
                if (this._offerPartClosureAcknowledgementKey === partClosureAcknowledgementKey) {
                    this._offerPartClosureAcknowledgementKey = undefined
                }
                console.error("Failed to mark offerer part-closure notifications as seen", error)
            })
        }
    }


    /**
     * A whole-procurement closure is visible on the general-information tab as
     * authoritative lifecycle state. Acknowledge only that event
     * family after the matching server snapshot has loaded; keep the optimistic
     * combined count unchanged because an unread amendment may share the same
     * per-procurement bucket until the server returns the exact split.
     */
    _markProcurementClosureNotificationSeenIfViewingDetails(procurementId: string) {
        if (
            this._currentTab !== "details" ||
            !this._isInOffererMode ||
            this._procurement?.status !== "closed-without-contract" ||
            this._unseenProcurementNotificationCount === 0
        ) {
            return
        }

        const currentProcurementId = UIRoute.currentRoute
            .componentWithViewController(ProcurementDetailsViewController)
            ?.parameters.id
        const notificationSnapshot = this._procurementDetailsNotificationSnapshot
        const activeCompanyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        if (
            currentProcurementId !== procurementId ||
            notificationSnapshot?.procurementId !== procurementId ||
            notificationSnapshot.companyId !== activeCompanyId
        ) {
            return
        }

        const acknowledgementKey = [
            activeCompanyId ?? "",
            procurementId,
            notificationSnapshot.auditCutoff
        ].join(":")
        if (this._procurementClosureAcknowledgementKey === acknowledgementKey) {
            return
        }
        this._procurementClosureAcknowledgementKey = acknowledgementKey

        const currentCombinedCount =
            this._unseenQANotificationCount +
            this._unseenOfferNotificationCount +
            this._unseenProcurementNotificationCount
        RHCore.sharedInstance.notificationStore.markSeen(
            procurementId,
            ["procurement.closed_without_contract"],
            currentCombinedCount,
            undefined,
            undefined,
            notificationSnapshot.auditCutoff
        ).then(() => {
            RHCore.sharedInstance.procurementStore.invalidatePublishedProcurements([procurementId])
        }).catch(error => {
            if (this._procurementClosureAcknowledgementKey === acknowledgementKey) {
                this._procurementClosureAcknowledgementKey = undefined
            }
            console.error("Failed to mark procurement closure notification as seen", error)
        })
    }


    _markOfferNotificationsSeenIfViewingOffers(procurementId: string) {
        if (this._currentTab === "offers" && this._isCurrentUserOnProcuringSide) {
            const offerSnapshot = this._offersView?.loadedNotificationSnapshot
            if (offerSnapshot) {
                this._markSubmittedOfferNotificationsAsSeenForProcurementWithSnapshot(procurementId, offerSnapshot)
            }
            return
        }

        this._markOffererNotificationsAsSeenForProcurementAfterContentLoad(procurementId)
    }


    // ─── Route & lifecycle ────────────────────────────────────────────────────
    
    private get _currentTab(): ProcurementTab {
        const tabParam = UIRoute.currentRoute
            .componentWithViewController(ProcurementDetailsViewController)
            ?.parameters.tab as ProcurementTab | undefined
        return tabParam ?? "details"
    }
    
    
    get _isInOffererMode(): boolean {
        return RHCore.sharedInstance.isInOffererMode
    }
    
    
    get _isProcurementOwnedByTheActiveCompany(): boolean {
        const procuringCompanyId = referenceId(this._procurement?.procuringCompany)
        return !!procuringCompanyId &&
            procuringCompanyId === RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
    }
    
    
    get _hasDetailedProcurementAccess(): boolean {
        if (!this._procurementCapabilities) {
            return NO
        }
        return this._procurementCapabilities.canViewDetailed === YES
    }
    
    
    private get _hasProcurerAnswerCapability(): boolean {
        if (!this._procurementCapabilities) {
            return NO
        }
        return this._procurementCapabilities.canAnswerMessages === YES
    }


    get _hasDetailedOffererTeamAccess(): boolean {
        if (!this._isInOffererMode || !this._isRegisteredAsTenderer) {
            return NO
        }

        if (this._makeOfferTabState === "empty") {
            return this._offerCreationCapabilities?.canViewDetailed === YES
        }

        return this._offerCapabilities?.teamRole === "responsible" ||
            this._offerCapabilities?.teamRole === "authorized"
    }
    
    
    async navigationWillReset(): Promise<boolean> {
        if (!this._hasUnsavedOfferData()) {
            return YES
        }
        return new Promise(resolve => {
            const shower = CBDialogViewShower.showQuestionDialogWithCloseButton(
                "Kas soovid pakkumuse salvestada?",
                () => resolve(NO)
            )
            shower.dialogView.view.yesButton.titleLabel.text = "Jah"
            shower.dialogView.view.noButton.titleLabel.text = "Ei"
            shower.dialogView.view.noButtonDismissesDialog = NO
            shower.dialogView.view.setNeedsLayoutUpToRootView()

            shower.noButtonWasPressed = () => {
                this._makeOfferView?.discardUnsavedChanges()
                this._roundWorkspace?.discardUnsavedChanges()
                resolve(YES)
                shower.dialogView.dismiss()
            }

            shower.yesButtonWasPressed = async () => {
                shower.dialogView.view.yesButton.enabled = NO
                shower.dialogView.view.noButton.enabled = NO
                shower.dialogView.view.closeButton.enabled = NO
                const saved = await this._saveUnsavedOfferDrafts()
                if (!saved) {
                    shower.dialogView.dismiss()
                    return
                }
                resolve(YES)
                shower.dialogView.dismiss()
            }
        })
    }


    async _saveUnsavedOfferDrafts(): Promise<boolean> {
        if (this._roundWorkspace?.hasUnsavedOfferData) {
            return await this._roundWorkspace.saveUnsavedOfferDrafts()
        }
        if (this._makeOfferView?.hasUnsavedOfferData) {
            return await this._makeOfferView.saveDraft()
        }
        return YES
    }
    
    
    /**
     * Returns YES when the user is on the make-offer tab, the active step is
     * the entry form (step 1), and it contains at least one unsaved offer card.
     * Steps 2 (confirm) and 3 (submitted) are already persisted server-side,
     * so navigating away from them is always safe.
     */
    private _hasUnsavedOfferData(): boolean {
        if (this._roundWorkspace?.hasUnsavedOfferData) {
            return YES
        }
        if (this._currentTab !== "makeOffer") {
            return NO
        }
        const makeOfferView = this._makeOfferView
        if (!makeOfferView) {
            return NO
        }
        // Only step 1 (EnterProcurementOfferView) contains unsaved local state
        if (makeOfferView.activeStepView !== makeOfferView.enterView) {
            return NO
        }
        return makeOfferView.enterView.hasSections &&
            makeOfferView.enterView.hasUnsavedSessionData
    }
    
    
    override async viewWillAppear() {
        await super.viewWillAppear()
        RHPageFocusManager.shared.registerContentControllerManager(this._focusManager)
        RHCore.sharedInstance.notificationStore.subscribe(this, (affectedIds) => {
            const procurementId = UIRoute.currentRoute
                .componentWithViewController(ProcurementDetailsViewController)
                ?.parameters.id
            if (procurementId && affectedIds.includes(procurementId)) {
                const notificationStore = RHCore.sharedInstance.notificationStore
                const qaCount = notificationStore.qaNotificationCount(procurementId)
                const offerCount = notificationStore.offerNotificationCount(procurementId)
                const procurementCount =
                    notificationStore.procurementNotificationCount(procurementId) ?? 0
                if (qaCount !== undefined && offerCount !== undefined) {
                    // A global acknowledgement may clear only one event family.
                    // Apply the exact server split so the sibling tab's valid marker
                    // remains while every active session clears the seen one.
                    this._unseenQANotificationCount = qaCount
                    this._unseenOfferNotificationCount = offerCount
                    this._unseenProcurementNotificationCount = procurementCount
                    this._updateTabButtonBadges()
                }
                else if (notificationStore.notificationCount(procurementId) === 0) {
                    // Compatibility fallback for counts produced before split
                    // acknowledgement state has reached this client.
                    this._unseenQANotificationCount = 0
                    this._unseenOfferNotificationCount = 0
                    this._unseenProcurementNotificationCount = 0
                    this._updateTabButtonBadges()
                }
            }
            // Offer message counts are keyed by offer ID, not procurement ID.
            // Re-evaluate the makeOffer tab badge whenever any of the current
            // user's offer IDs appear in the affected set.
            const userOfferIds = this._userOffers.map(offer => offer._id + "")
            const offerMessageCountAffected =
                affectedIds.length === 0 ||
                userOfferIds.some(offerId => affectedIds.includes(offerId))
            if (offerMessageCountAffected) {
                this._updateTabButtonBadges()
            }
        })
        RHCore.sharedInstance.offerStore.subscribe(this, (changedProcurementIds = []) => {
            if (this._roundContext.hasRounds) {
                this._roundWorkspace?.invalidateOfferContentForProcurements(changedProcurementIds)
                this._configureRoundWorkspaceForCurrentRoute()
                this._updateMakeOfferTabButtonTitle()
            }
            const currentProcurementId = UIRoute.currentRoute
                .componentWithViewController(ProcurementDetailsViewController)
                ?.parameters.id
            const currentProcurementOfferChanged =
                changedProcurementIds.length === 0 ||
                (
                    currentProcurementId !== undefined &&
                    changedProcurementIds.includes(currentProcurementId)
                )
            if (
                currentProcurementId &&
                currentProcurementId === this._contentProcurementId &&
                currentProcurementOfferChanged
            ) {
                if (
                    (this._currentTab === "makeOffer" || this._currentTab === "team") &&
                    this._isInOffererMode
                ) {
                    void this._reloadOffererNotificationContent().then(didLoad => {
                        const routeProcurementId = UIRoute.currentRoute
                            .componentWithViewController(ProcurementDetailsViewController)
                            ?.parameters.id
                        if (didLoad === YES && routeProcurementId === currentProcurementId) {
                            // The visible offer state can change before its split count refreshes.
                            this._markOffererNotificationsAsSeenForProcurementAfterContentLoad(
                                currentProcurementId,
                                YES
                            )
                        }
                    })
                }
                else {
                    // A detached offer tree must reload before acknowledging its next event.
                    this._invalidateOffererNotificationContent()
                }
            }

            // MUST NOT call offerStore.invalidateIncomingOffers() here: it calls
            // _notifySubscribers(), which re-invokes this very handler — an infinite
            // re-entrant notification loop. The incoming cache was already invalidated
            // by the push handler that fired this notification (_onIncomingOffersDidChange).
            //
            // Only force a refresh here when the offers view is detached (a different
            // tab is currently shown), so its cached data isn't stale next time the
            // user opens the offers tab. While it is mounted, RHOffersEvaluationView's
            // own offerStore subscription (registered in its wasAddedToViewTree)
            // already refreshes its data in place — reassigning .procurement here too
            // would undo that by forcing a full teardown-and-rebuild of every
            // evaluation section on every single evaluation action.
            if (this._offersView && this._procurement && this._offersView.superview !== this.view &&
                isFullProcurement(this._procurement)) {
                this._offersView.procurement = this._procurement
            }
        })
        RHCore.sharedInstance.procurementStore.subscribe(this, (changedIds: string[]) => {
            const currentProcurementId = UIRoute.currentRoute
                .componentWithViewController(ProcurementDetailsViewController)
                ?.parameters.id
            if (this._roundContext.hasRounds && changedIds.some(procurementId =>
                this._roundContext.informationByProcurementId.has(procurementId)
            )) {
                void this._loadProcurementData()
                return
            }
            if (!currentProcurementId || !changedIds.includes(currentProcurementId)) {
                return
            }
            this._invalidateOffererNotificationContent()
            
            // While the offers tab is mounted and showing live evaluation work, a
            // A full _loadProcurementData() call is disruptive:
            // it shows the page-wide spinner, refetches QA/contracts/make-offer data nobody is looking at
            // right now, and reassigns the offers view's whole procurement — tearing
            // down every evaluation section. Only fresh access capabilities and
            // procurement metadata (status, partWinnerOfferIds) need to reach the
            // existing view; offers/decision data is already kept in sync by its own
            // offerStore subscription.
            if (
                this._offersView &&
                this._offersView.superview === this.view &&
                this._contentProcurementId === currentProcurementId
            ) {
                const activeCompanyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
                const applicationMode = RHCore.sharedInstance.applicationMode
                void RHCore.sharedInstance.procurementStore
                    .retrieveProcurementInformation(currentProcurementId)
                    .then(async information => {
                        const routeProcurementIdAfterRefresh = UIRoute.currentRoute
                            .componentWithViewController(ProcurementDetailsViewController)
                            ?.parameters.id
                        if (
                            routeProcurementIdAfterRefresh !== currentProcurementId ||
                            this._contentProcurementId !== currentProcurementId ||
                            RHCore.sharedInstance.companyAffiliationStore.activeCompanyId !== activeCompanyId ||
                            RHCore.sharedInstance.applicationMode !== applicationMode
                        ) {
                            return
                        }

                        const freshCapabilities = information.capabilities
                        if (freshCapabilities?.canViewDetailed !== YES) {
                            await this._loadProcurementData()
                            return
                        }

                        this._procurementCapabilities = freshCapabilities
                        this._openingProtocolSharing = information.openingProtocolSharing
                        this._openingProtocolOfferCount = information.offerCount
                        if (this._offersView) {
                            this._offersView.procurementCapabilities = freshCapabilities
                        }
                        const detailsView = this._loadDetailsView()
                        detailsView.configureWithCurrentUserTeamRole(freshCapabilities.teamRole)
                        const freshProcurement = information.procurement
                        const mergedProcurement = this._procurementPublicByMergingUpdate(freshProcurement)
                        this._procurement = mergedProcurement
                        this._offersView?.refreshProcurementMetadata(freshProcurement)
                        detailsView.procurementData = this._procurementDataForDetailsView(mergedProcurement)
                        this._updateContentForCurrentMode()
                        this._updateTabButtonCounts(information.activePartOfferCount)
                    })
                    .catch(
                        error => console.error("Failed to refresh procurement metadata after push", error)
                    )
                return
            }
            
            this._loadProcurementData().catch(
                error => console.error("Failed to reload procurement data after push", error)
            )
        })
        RHCore.sharedInstance.qaStore.subscribe(this, (procurementId: string) => {
            const currentProcurementId = UIRoute.currentRoute
                .componentWithViewController(ProcurementDetailsViewController)
                ?.parameters.id
            if (currentProcurementId && (procurementId === currentProcurementId ||
                this._roundContext.informationByProcurementId.has(procurementId))) {
                this._loadProcurementData().then(didLoadQAContent => {
                    if (didLoadQAContent === YES && this._currentTab === "qa") {
                        this._markQANotificationsAsSeenForProcurement(procurementId)
                    }
                }).catch(
                    error => console.error("Failed to reload procurement QA notification state", error)
                )
            }
        })
        RHCore.sharedInstance.privateMessageStore.subscribe(this, (
            procurementId: string,
            shouldReload: boolean
        ) => {
            const currentProcurementId = UIRoute.currentRoute
                .componentWithViewController(ProcurementDetailsViewController)
                ?.parameters.id
            if (!currentProcurementId || (procurementId !== currentProcurementId &&
                !this._qaView?.communicationLabelsByProcurementId.has(procurementId))) {
                return
            }
            if (shouldReload && this._canSeeQA) {
                void this._loadQAView().loadPrivateCommunicationSummary()
            }
            else {
                this._qaView?.invalidatePrivateCommunicationSummary()
            }
        })
    }
    
    override async viewWillDisappear() {
        await super.viewWillDisappear()
        this._companyInvitationDialog?.dismiss()
        this._companyInvitationDialog = undefined
        // A detached offer view is only a snapshot and must revalidate on its next appearance.
        this._invalidateOffererNotificationContent()
        this._procurementDetailsNotificationSnapshot = undefined
        RHPageFocusManager.shared.deregisterContentControllerManager(this._focusManager)
        RHCore.sharedInstance.notificationStore.unsubscribe(this)
        RHCore.sharedInstance.offerStore.unsubscribe(this)
        RHCore.sharedInstance.procurementStore.unsubscribe(this)
        RHCore.sharedInstance.qaStore.unsubscribe(this)
        RHCore.sharedInstance.privateMessageStore.unsubscribe(this)
    }
    
    override async viewDidAppear() {
        await super.viewDidAppear()
        const didLoadQAContent = await this._loadProcurementData()
        
        const procurementId = UIRoute.currentRoute
            .componentWithViewController(ProcurementDetailsViewController)
            ?.parameters.id
        if (procurementId) {
            // Mark audit log events as seen whenever the procurement page opens.
            RHCore.sharedInstance.notificationStore.markEventsSeen(procurementId)
            // Mark QA notifications as seen only when the QA tab is already the
            // initial tab. For all other entry points the badge stays until the
            // user explicitly opens the "Küsimused ja vastused" tab.
            if (didLoadQAContent === YES && this._currentTab === "qa") {
                this._markQANotificationsAsSeenForProcurement(procurementId)
            }
            // Same idea for offer notifications: the unread emphasis clears
            // only when the offers tab is actually viewed.
            this._markOfferNotificationsSeenIfViewingOffers(procurementId)
        }
        
        // Load the offer-keyed unread-message counts so the "Minu pakkumus" tab
        // and per-part "Privaatsed sõnumid" accordion titles are accurate.
        RHCore.sharedInstance.notificationStore.reloadOfferMessageCounts()
        
        this.view.setNeedsLayoutUpToRootView()
    }
    
    
    override viewDidReceiveBroadcastEvent(event: { name: string }) {
        super.viewDidReceiveBroadcastEvent(event)
        if (event.name === RHCore.broadcastEventName.applicationModeDidChange) {
            this._invalidateOffererNotificationContent()
            this._updateContentForCurrentMode()
            if (this._contentProcurementId) {
                this._markProcurementClosureNotificationSeenIfViewingDetails(
                    this._contentProcurementId
                )
            }
            this.view.setNeedsLayoutUpToRootView()
        }
        if (event.name === RHCore.broadcastEventName.activeCompanyDidChange) {
            this._companyInvitationDialog?.dismiss()
            this._companyInvitationDialog = undefined
            // Every company-scoped field on _procurement (capabilities, userOffers,
            // isCurrentUserCompanyRegisteredAsTenderer, the conflict banner, the
            // make-offer view's own data) was resolved for whichever company was
            // active at the last fetch — reload for real rather than just
            // recomputing visibility from what is now a stale snapshot.
            this._clearProcurementContent()
            this._offererInformativeStatus = undefined
            this._offererSubmissionOutcome = undefined
            this._updateContentForCurrentMode()
            void this._loadProcurementData()
        }
        if (event.name === RHCore.broadcastEventName.procurementDeadlineChanged) {
            const payload = broadcastPayloadOf<RHProcurementDeadlineChangedPayload>(event)
            // Only show the dialog when this VC is displaying the affected procurement
            if (payload && payload.procurementId === this._procurement?._id?.toString()) {
                this._showDeadlineChangedNotificationDialog(payload)
            }
        }
        if (event.name === RHCore.broadcastEventName.procurementAmended) {
            const payload = broadcastPayloadOf<RHProcurementAmendedPayload>(event)
            if (payload && payload.procurementId === this._procurement?._id?.toString()) {
                this._showProcurementAmendedNotificationDialog(payload)
            }
        }
    }
    
    
    async _loadProcurementData(): Promise<boolean> {
        let didLoadQAContent = NO
        const loadGeneration = ++this._procurementDataLoadGeneration
        this._procurementDetailsNotificationSnapshot = undefined
        try {
            this.view.loading = YES
            
            const routeComponent = UIRoute.currentRoute.componentWithViewController(
                ProcurementDetailsViewController
            )
            const procurementID = routeComponent?.parameters.id
            const activeCompanyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
            const isLoadCurrent = () => {
                const currentRouteComponent = UIRoute.currentRoute.componentWithViewController(
                    ProcurementDetailsViewController
                )
                return (
                    loadGeneration === this._procurementDataLoadGeneration &&
                    currentRouteComponent?.parameters.id === procurementID &&
                    RHCore.sharedInstance.companyAffiliationStore.activeCompanyId === activeCompanyId
                )
            }
            
            if (!procurementID) {
                console.error("No procurement ID provided")
                this._showProcurementUnavailableState()
                return NO
            }
            
            const result = await RHCore.sharedInstance.procurementStore
                .retrieveProcurementInformation(procurementID)
            
            // Stale-response guard: bail if the route changed while the retrieve
            // was in flight, so a slower response cannot render under a newer route.
            if (!isLoadCurrent()) {
                return NO
            }
            
            if (!result.procurement) {
                this._showProcurementUnavailableState()
                return NO
            }
            if (
                result.notificationStateGeneration !==
                RHCore.sharedInstance.notificationStore.notificationStateGeneration
            ) {
                return await this._loadProcurementData()
            }
            
            const reconciliationRoundChain = await this._loadReconciliationRoundChain(procurementID, isLoadCurrent)
            if (!reconciliationRoundChain) {
                return NO
            }
            if (result.procurement.reconciliationParentProcurement) {
                let rootInformation = result
                const ancestors = new Set<string>([procurementID])
                let parentId = referenceId(rootInformation.procurement.reconciliationParentProcurement)
                while (parentId) {
                    if (ancestors.has(parentId)) {
                        this._showProcurementUnavailableState()
                        return NO
                    }
                    ancestors.add(parentId)
                    rootInformation = await RHCore.sharedInstance.procurementStore.retrieveProcurementInformation(parentId)
                    if (!isLoadCurrent()) {
                        return NO
                    }
                    parentId = referenceId(rootInformation.procurement.reconciliationParentProcurement)
                }
                const rootId = rootInformation.procurement._id?.toString()
                if (!rootId) {
                    this._showProcurementUnavailableState()
                    return NO
                }
                UIRoute.currentRoute
                    .routeBySettingParameterInComponent(ProcurementDetailsViewController.routeComponentName, "id", rootId)
                    .routeByRemovingParameterInComponent(ProcurementDetailsViewController.routeComponentName, "part")
                    .routeBySettingParameterInComponent(ProcurementDetailsViewController.routeComponentName, "round", procurementID)
                    .applyByReplacingCurrentRouteInHistory()
                return NO
            }
            let communicationProcurementIds: string[] = []
            if ((result.isCurrentUserOnProcuringSide && result.capabilities?.canViewDetailed === YES) ||
                result.offerCapabilities?.teamRole === "responsible" || result.offerCapabilities?.teamRole === "authorized" ||
                result.offerCreationCapabilities?.canViewDetailed === YES) {
                try {
                    const communication = await RHCore.sharedInstance.qaStore.retrieveMergedQuestions(procurementID)
                    communicationProcurementIds = communication.rounds.map(round => round.procurementId)
                }
                catch (error) {
                    console.error("Communication round discovery failed:", error)
                }
                if (!isLoadCurrent()) {
                    return NO
                }
            }
            if (!await this._roundContext.loadForProcurement(
                result, reconciliationRoundChain, isLoadCurrent, routeComponent?.parameters.round, communicationProcurementIds
            )) {
                return NO
            }
            this._isCurrentUserOnProcuringSide = result.isCurrentUserOnProcuringSide
            this._procurement = result.procurement as RHProcurementPublic
            this._procurementLifecycleState = result.lifecycleState
            this._procurerInformativeStatus = result.procurerInformativeStatus
            this._offererInformativeStatus = result.offererInformativeStatus
            this._offererSubmissionOutcome = result.offererSubmissionOutcome
            this._showsProcurementUnavailableState = NO
            this.procurementUnavailableBanner.removeFromSuperview()
            this.procurementAccessExplanationLabel.removeFromSuperview()
            this._procurementCapabilities = result.capabilities
            this._procurementEngagementSummary =
                result.engagementSummary ?? (
                    "engagementSummary" in result.procurement
                    ? result.procurement.engagementSummary as RHProcurementEngagementSummary | undefined
                    : undefined
                )

            if (!this._isInOffererMode && this._procurementCapabilities?.role &&
                this._procurementCapabilities.role !== "none") {
                await this.conflictOfInterestBannerView.load({
                    procurementId: procurementID,
                    fromSide: "procurement"
                })
            }
            else {
                this.conflictOfInterestBannerView.clear()
            }
            if (!isLoadCurrent()) {
                return NO
            }
            if (result.isCurrentUserOnProcuringSide) {
                this._userOffers = []
            }
            else {
                this._userOffers = result.userOffers
            }
            this._offerCapabilities = result.offerCapabilities
            this._offerCreationCapabilities = result.offerCreationCapabilities
            this._canWithdrawTendererRegistration = result.canWithdrawTendererRegistration
            this._openingProtocolOfferCount = result.offerCount
            this._openingProtocolSharing = result.openingProtocolSharing
            this._syncMakeOfferTabStateFromUserOffers()
            this._unseenQANotificationCount = result.unseenQANotificationCount
            this._unseenOfferNotificationCount = result.unseenOfferNotificationCount
            this._unseenProcurementNotificationCount = result.unseenProcurementNotificationCount
            if (result.requiresProcurerAttention) {
                this._requiresProcurerAttention = YES
            }
            else {
                this._requiresProcurerAttention = NO
            }
            if (result.requiresProcurerQAAttention) {
                this._requiresProcurerQAAttention = YES
            }
            else {
                this._requiresProcurerQAAttention = NO
            }
            RHCore.sharedInstance.notificationStore.setCount(
                procurementID,
                this._unseenQANotificationCount +
                this._unseenOfferNotificationCount +
                this._unseenProcurementNotificationCount
            )
            
            this._updateTabButtonBadges()
            this._updatePageTitle()
            this._updateLifecyclePresentationInDetailsView()
            const detailsView = this._loadDetailsView()
            detailsView.configureWithCurrentUserTeamRole(this._procurementCapabilities?.teamRole)
            detailsView.procurementData = this._procurementDataForDetailsView(
                result.procurement as RHProcurementPublic
            )
            this._procurementDetailsNotificationSnapshot = undefined
            if (
                result.notificationSnapshotAt &&
                !Number.isNaN(new Date(result.notificationSnapshotAt).getTime())
            ) {
                this._procurementDetailsNotificationSnapshot = {
                    companyId: activeCompanyId,
                    procurementId: procurementID,
                    auditCutoff: result.notificationSnapshotAt,
                    loadedTabs: new Set(["details"])
                }
            }
            this._markProcurementClosureNotificationSeenIfViewingDetails(procurementID)
            this._updateProcurementActionAvailability()
            this._updateOpeningProtocolExportRow()
            
            const activeCompanyIdForMakeOfferGuard = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
            if (
                this._shouldLoadOffererContent &&
                (
                    this._makeOfferViewLoadedForCompanyId !== activeCompanyIdForMakeOfferGuard ||
                    !this._offererContentLoadNotificationContext
                )
            ) {
                // Claim the guard BEFORE awaiting. loadForProcurement is async, and during
                // its await window _updateContentForCurrentMode (applicationModeDidChange
                // broadcast) or a second _loadProcurementData
                // call from handleRoute would otherwise still see the guard as unclaimed
                // for this company and start a concurrent
                // second load. Two overlapping loads each clear-then-append one section per
                // part, duplicating "Osa N".
                this._makeOfferViewLoadedForCompanyId = activeCompanyIdForMakeOfferGuard
                await this._reloadOffererNotificationContent()
            }
            
            if (this._canSeeQA) {
                const qaView = this._loadQAView()
                qaView.roundContext = this._roundContext
                qaView.configureAccessState({
                    isCurrentUserActingAsProcurer: result.isCurrentUserOnProcuringSide && !this._isInOffererMode,
                    isRegisteredTenderer: this._isRegisteredAsTenderer,
                    canAnswerMessages: this._hasProcurerAnswerCapability,
                    shouldReloadVisiblePrivateView: NO
                })
                didLoadQAContent = await qaView.loadQuestionsForProcurement(
                    result.procurement,
                    reconciliationRoundChain,
                    isLoadCurrent
                )
                if (!isLoadCurrent()) {
                    return NO
                }
                
                const questionId = UIRoute.currentRoute
                    .componentWithViewController(ProcurementDetailsViewController)
                    ?.parameters.questionId as string | undefined
                if (questionId) {
                    qaView.highlightQuestion(questionId)
                }

            }
            
            if (this._shouldShowIncomingOffersPanel) {
                // offersView.procurement expects the full private RHProcurement
                const offersView = this._loadOffersView()
                offersView.procurementCapabilities = this._procurementCapabilities
                offersView.procurement = result.procurement as RHProcurement
            }
            
            if (this._shouldLoadContracts) {
                const contractsView = this._loadContractsView()
                const procurement = result.procurement as RHProcurementPublic
                await contractsView.loadContractsForProcurement(
                    procurement,
                    this._isCurrentUserOnProcuringSide,
                    this._procurementCapabilities?.canEdit === YES
                )
                if (isLoadCurrent() && contractsView.hasLoadedContractsForProcurement(procurement)) {
                    this._procurementDetailsNotificationSnapshot?.loadedTabs.add("contracts")
                }
            }
            
            if (this._shouldShowProcurementTeamPanel) {
                await this._loadProcurementTeamView(procurementID)
                if (isLoadCurrent()) {
                    this._procurementDetailsNotificationSnapshot?.loadedTabs.add("team")
                }
            }

            if (!isLoadCurrent()) {
                return NO
            }
            
            this._contentProcurementId = procurementID
            this._configureRoundWorkspaceForCurrentRoute()
            this._updateMakeOfferTabButtonTitle()
            this._updateContentForCurrentMode()
            this._consumePrivateMessageDeepLinkForCurrentRoute(procurementID)
            this._markOffererNotificationsAsSeenForProcurementAfterContentLoad(procurementID)
            this._markProcurementClosureNotificationSeenIfViewingDetails(procurementID)
            
            this._updateTabButtonCounts(result.activePartOfferCount)
            
            this.view.setNeedsLayout()
            this._markPendingNotificationAsReadForVisibleContent()
            return didLoadQAContent
        }
        catch (error) {
            console.error("Error loading procurement data:", error)
            if (!this._procurement) {
                this._showProcurementUnavailableState()
            }
            return NO
        }
        finally {
            if (loadGeneration === this._procurementDataLoadGeneration) {
                this.view.loading = NO
            }
        }
    }
    
    
    get _canSeeQA(): boolean {
        if (!this._procurement) {
            return NO
        }
        
        // Owners always see Q&A regardless of procurement status
        if (this._isCurrentUserOnProcuringSide) {
            return this._hasDetailedProcurementAccess
        }
        
        return this._hasDetailedOffererTeamAccess
    }
    
    
    get _canAskQuestion(): boolean {
        if (this._qaView && this._qaView.selectedSubtabKey !== "public") {
            return NO
        }
        if (!this._isInOffererMode) {
            return NO
        }
        if (!this._qaView?._composeTargets.length) {
            return NO
        }
        if (!this._hasDetailedOffererTeamAccess) {
            return NO
        }
        if (this._isProcurementOwnedByTheActiveCompany) {
            return NO
        }
        
        // Must be a registered tenderer to ask questions
        const publicProcurement = this._procurement as RHProcurementPublic
        if (!publicProcurement.isCurrentUserCompanyRegisteredAsTenderer) {
            return NO
        }
        
        return YES
    }
    
    
    /** Broadcast availability follows the server's communication targets and procurer capabilities. */
    private get _canCurrentUserSendProcurerMessage(): boolean {
        if (this._qaView && this._qaView.selectedSubtabKey !== "public") {
            return NO
        }
        if (this._isInOffererMode) {
            return NO
        }
        if (!this._isCurrentUserOnProcuringSide) {
            return NO
        }
        if (!this._qaView?._composeTargets.length) {
            return NO
        }
        return this._hasProcurerAnswerCapability
    }
    
    
    private get _isRegisteredAsTenderer(): boolean {
        const publicProcurement = this._procurement as RHProcurementPublic | undefined
        return publicProcurement?.isCurrentUserCompanyRegisteredAsTenderer ?? NO
    }
    
    
    /**
     * Whether the "register as tenderer" button should be visible.
     * Shown when the user is in offerer mode, is logged in, is not the owner,
     * and has not yet registered for this procurement.
     */
    private get _shouldShowRegisterAsTendererButton(): boolean {
        if (!this._isInOffererMode) {
            return NO
        }
        
        if (!RHCore.sharedInstance.userProfile) {
            return NO
        }
        
        if (!this._isUserAffiliated) {
            return NO
        }
        
        if (!this._procurement || this._procurement.status !== "published") {
            return NO
        }
        
        if (this._isProcurementOwnedByTheActiveCompany) {
            return NO
        }
        
        if (this._isRegisteredAsTenderer) {
            return NO
        }

        if (this._offerCreationCapabilities?.canCreateOffer !== YES) {
            return NO
        }
        
        return YES
    }
    
    
    /**
     * Whether the log-in prompt should be visible.
     * Shown when there is no authenticated user session and the procurement is published.
     */
    private get _shouldShowLoginButton(): boolean {
        if (RHCore.sharedInstance.userProfile) {
            return NO
        }
        
        if (!this._procurement || this._procurement.status !== "published") {
            return NO
        }
        
        return YES
    }
    
    
    /**
     * True when the logged-in user belongs to a company.
     * Reads the live company-affiliation store, not userProfile.organization —
     * that field is a login-time snapshot never updated by the affiliation-approval
     * socket push, so it goes stale until the next full page load/re-login.
     */
    private get _isUserAffiliated(): boolean {
        return !!RHCore.sharedInstance.companyAffiliationStore.activeCompanyAffiliation?.company
    }
    
    
    /**
     * Whether the "join a company" prompt should be visible.
     * Shown when the user is logged in, has no company affiliation,
     * and the procurement is published.
     */
    private get _shouldShowUnaffiliatedButton(): boolean {
        if (!RHCore.sharedInstance.userProfile) {
            return NO
        }
        
        if (this._isUserAffiliated) {
            return NO
        }
        
        if (!this._procurement || this._procurement.status !== "published") {
            return NO
        }
        
        return YES
    }
    
    
    private _confirmRegisterAsTenderer() {
        if (this._offerSubmissionDeadlineHasPassed()) {
            CBDialogViewShower.alert("Pakkumuste esitamise tähtaeg on möödas.")
            return
        }
        const confirmationText = "Kas soovid hankele registreeruda?"
        const shower = CBDialogViewShower.showQuestionDialog(confirmationText)
        
        shower.dialogView.view.titleLabel.localizedTextObject = nil
        shower.dialogView.view.titleLabel.text = ""
        shower.dialogView.view.questionLabel.text = confirmationText
        
        shower.dialogView.view.yesButton.titleLabel.text = "Jah"
        shower.dialogView.view.noButton.titleLabel.text = "Tühista"
        shower.dialogView.view.setNeedsLayoutUpToRootView()
        
        shower.yesButtonWasPressed = async () => {
            shower.dialogView.dismiss()
            await this._registerAsTenderer()
        }
    }
    
    
    private async _registerAsTenderer() {
        if (!this._procurement?._id) {
            return
        }
        const procurementId = this._procurement._id + ""
        
        try {
            this.registerAsTendererButton.loading = YES
            this.makeOfferTabButton.loading = YES
            
            // Registration goes through the procurement store so the
            // published-procurements caches are invalidated and the
            // "Avaldatud hanked" table shows the registered state (row tint +
            // "Hankele registreeritud") without a page refresh.
            await RHCore.sharedInstance.procurementStore
                .registerAsTendererForProcurement(procurementId)
            
            // Re-fetch the full procurement via RetrieveProcurementInformation so
            // that this._procurement is the richly-hydrated object (with
            // parts[].requiredDocuments populated) before loadForProcurement is
            // called on the make-offer view. Using the thin RHProcurementPublic
            // returned directly by RegisterAsTendererForProcurement caused
            // requiredDocuments to be missing, which prevented document-slot
            // rendering and made offer submission impossible without a page refresh.
            await this._loadProcurementData()
        }
        catch (error) {
            console.error("Error registering as tenderer:", error)
            let errorMessage = "Hankele registreerumine ebaõnnestus."
            if (error instanceof Error && error.message) {
                errorMessage = error.message
            }
            CBDialogViewShower.alert(errorMessage)
        }
        finally {
            this.registerAsTendererButton.loading = NO
            this.makeOfferTabButton.loading = NO
        }
    }
    
    
    _confirmWithdrawalOfTendererRegistrationForProcurement(procurementId: string, button: RHButton) {
        if (!this._isInOffererMode || button.hidden || button.loading) {
            return
        }
        const companyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        const routeProcurementId = UIRoute.currentRoute.componentWithViewController(
            ProcurementDetailsViewController
        )?.parameters.id
        const shower = CBDialogViewShower.showQuestionDialog(
            "Kas soovid oma ettevõtte hankele registreerimise tühistada?"
        )
        shower.dialogView.view.configureWithObject({
            yesButton: { titleLabel: { text: "Tühista registreerimine" } },
            noButton: { titleLabel: { text: "Tagasi" } }
        })
        shower.dialogView.view.setNeedsLayoutUpToRootView()
        shower.yesButtonWasPressed = async () => {
            shower.dialogView.dismiss()
            if (button.loading || button.hidden || !button.isMemberOfViewTree || !this._isInOffererMode ||
                companyId !== RHCore.sharedInstance.companyAffiliationStore.activeCompanyId ||
                routeProcurementId !== UIRoute.currentRoute.componentWithViewController(
                    ProcurementDetailsViewController
                )?.parameters.id) {
                return
            }
            button.loading = YES
            try {
                await RHCore.sharedInstance.procurementStore
                    .withdrawTendererRegistrationForProcurement(procurementId)
                if (companyId === RHCore.sharedInstance.companyAffiliationStore.activeCompanyId &&
                    routeProcurementId === UIRoute.currentRoute.componentWithViewController(
                        ProcurementDetailsViewController
                    )?.parameters.id) {
                    button.hidden = YES
                    if (procurementId === routeProcurementId) {
                        this._canWithdrawTendererRegistration = NO
                        this._procurement = undefined
                    }
                    await this._loadProcurementData()
                }
            }
            catch (error) {
                let errorMessage = "Hankele registreerimise tühistamine ebaõnnestus."
                if (error instanceof Error && error.message) {
                    errorMessage = error.message
                }
                CBDialogViewShower.alert(errorMessage)
            }
            finally {
                button.loading = NO
            }
        }
    }


    _confirmConflictOfInterest() {
        if (!this._procurement?._id) {
            return
        }

        const confirmationText = "Kinnitan, et mul puudub selle hankega seotud huvide konflikt."
        const shower = CBDialogViewShower.showQuestionDialogWithSupportingText(
            "Huvide konflikti kinnitus",
            confirmationText
        )

        shower.dialogView.view.yesButton.titleLabel.text = "Kinnitan"
        shower.dialogView.view.noButton.titleLabel.text = "Tühista"
        shower.dialogView.view.setNeedsLayoutUpToRootView()

        shower.yesButtonWasPressed = async () => {
            shower.dialogView.dismiss()
            await this._applyConflictOfInterestConfirmation()
        }
    }


    private async _applyConflictOfInterestConfirmation() {
        const procurementId = this._procurement?._id?.toString()
        if (!procurementId) {
            return
        }
        
        try {
            this.conflictConfirmationButton.loading = YES
            await RHCore.sharedInstance.procurementStore.confirmProcurementConflictOfInterest(procurementId)
            // Reload so the freshly granted capabilities (canViewDetailed) flow in and the
            // now-unlocked Pakkumused / Teabevahetus tabs and their data are populated.
            await this._loadProcurementData()
            this.view.setNeedsLayoutUpToRootView()
        }
        catch (error) {
            console.error("Error confirming conflict of interest:", error)
            CBDialogViewShower.alert("Huvide konflikti kinnitamine ebaõnnestus.")
        }
        finally {
            this.conflictConfirmationButton.loading = NO
        }
    }
    
    
    /**
     * Once the procurement is loaded the page title shows its name, e.g.
     * `Hanke "Ehitustööd" toimingud` — for the procuring owner AND for offerers
     * (both act on the procurement here). The generic "Hanke üldandmed" is only
     * the not-yet-loaded fallback.
     */
    private _updatePageTitle() {
        if (this._showsProcurementUnavailableState) {
            this.titleLabel.text = "Hange ei ole kättesaadav"
            return
        }
        const procurementName = this._procurement?.title
        if (procurementName) {
            this.titleLabel.text = `Hanke "${RHTextSanitizer.escapedHTML(procurementName)}" toimingud`
        }
        else {
            this.titleLabel.text = "Hanke üldandmed"
        }
    }


    _updateBreadcrumbs() {
        const route = UIRoute.currentRoute
        const searchRoute = route
            .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
            .routeWithViewControllerComponent(ProcurementSearchViewController, nil)
        const items: RHBreadcrumbItem[] = [{ title: "Otsing", route: searchRoute }]
        let currentLocation = "Hange"
        const procurement = this._procurement
        const routeProcurementId = route.componentWithViewController(ProcurementDetailsViewController)?.parameters.id

        if (this._showsProcurementUnavailableState) {
            currentLocation = "Hange ei ole kättesaadav"
        }
        else if (procurement && procurement._id?.toString() === routeProcurementId) {
            let procurementRoute = route.routeBySettingParameterInComponent(
                ProcurementDetailsViewController.routeComponentName,
                ProcurementDetailsViewController.ParameterIdentifierName.tab,
                "details"
            )
            for (const parameterName of ["part", "round", "questionId", "qaSubtab", "privateMessageCompanyId"]) {
                procurementRoute = procurementRoute.routeByRemovingParameterInComponent(
                    ProcurementDetailsViewController.routeComponentName,
                    parameterName
                )
            }
            let procurementTitle = "Hange"
            if (procurement.referenceNumber) {
                procurementTitle += ` ${procurement.referenceNumber}`
            }
            items.push({ title: procurementTitle, route: procurementRoute })
            const tabButton = this.tabBar.tabButtonForKey(this._currentTab) ?? this.detailsTabButton
            currentLocation = RHTextSanitizer.plainText(tabButton.titleLabel.text)
        }

        items.push({ title: currentLocation })
        this.breadcrumbView.items = items
    }


    _updateLifecyclePresentationInDetailsView(detailsView?: RHProcurementDetailsView) {
        if (!this._procurement || this._showsProcurementUnavailableState) {
            return
        }

        const lifecycleState = this._procurementLifecycleState ?? "preparing"
        let informativeStatusText: string | undefined
        if (this._isInOffererMode) {
            informativeStatusText = RHProcurementLifecyclePresentation.presentationForOffererRow({
                lifecycleState,
                offererInformativeStatus: this._offererInformativeStatus,
                offererSubmissionOutcome: this._offererSubmissionOutcome
            }).secondaryText
        }
        else if (this._isCurrentUserOnProcuringSide && this._isProcurementOwnedByTheActiveCompany &&
            this._procurerInformativeStatus?.kind !== "viewOffers") {
            informativeStatusText = RHProcurementLifecyclePresentation.presentationForProcurerRow({
                lifecycleState,
                procurerInformativeStatus: this._procurerInformativeStatus
            }).secondaryText
        }

        detailsView ??= this._loadDetailsView()
        detailsView.procurementInformativeStatusText = informativeStatusText
        detailsView.procurementLifecycleState = lifecycleState
    }
    
    
    /**
     * Handles mode-change side effects that don't belong in the layout pass:
     * redirects away from tabs that are no longer reachable.
     */
    _updateContentForCurrentMode() {
        if (this._qaView) {
            this._qaView.configureAccessState({
                isCurrentUserActingAsProcurer: this._isCurrentUserOnProcuringSide && !this._isInOffererMode,
                isRegisteredTenderer: this._isRegisteredAsTenderer,
                canAnswerMessages: this._hasProcurerAnswerCapability
            })
        }
        
        this._updateProcurementActionAvailability()
        this._updateOpeningProtocolExportRow()
        this._updateOfferSubmissionActionStates()
        this._updatePageTitle()
        this._updateLifecyclePresentationInDetailsView()
        
        if (!this._procurement) {
            return
        }

        const currentTab = this._currentTab
        if (currentTab === "makeOffer" && this._shouldShowRegisterAsTendererButton) {
            this._showTab("details")
            return
        }
        if (
            currentTab === "makeOffer" &&
            !this._shouldShowMakeOfferTab &&
            this._shouldShowOffererTeamPanel
        ) {
            this._showTab("team")
            return
        }

        const isTabUnavailable =
            (currentTab === "team" && !this._shouldShowTeamPanel) ||
            (currentTab === "makeOffer" && !this._shouldShowMakeOfferTab) ||
            (currentTab === "offers" && !this._shouldShowIncomingOffersPanel) ||
            (currentTab === "qa" && !this._canSeeQA) ||
            (currentTab === "contracts" && !this._shouldShowContractsTab)
        
        if (isTabUnavailable) {
            this._showTab("details")
        }
        
        const activeCompanyIdForMakeOfferGuard = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        if (
            this._shouldLoadOffererContent &&
            (
                this._makeOfferViewLoadedForCompanyId !== activeCompanyIdForMakeOfferGuard ||
                !this._offererContentLoadNotificationContext
            ) &&
            this._procurement
        ) {
            this._makeOfferViewLoadedForCompanyId = activeCompanyIdForMakeOfferGuard
            this._reloadOffererNotificationContent().then(didLoad => {
                if (didLoad === YES && this._contentProcurementId) {
                    this._markOffererNotificationsAsSeenForProcurementAfterContentLoad(this._contentProcurementId)
                }
                this.view.setNeedsLayoutUpToRootView()
            })
        }
    }


    _showProcurementUnavailableState() {
        this._showsProcurementUnavailableState = YES
        this._updatePageTitle()
        this._updateLifecyclePresentationInDetailsView()
        this.view.setNeedsLayoutUpToRootView()
    }
    
    
    private get _shouldShowMakeOfferPanel(): boolean {
        if (!this._isInOffererMode) {
            return NO
        }
        
        if (!RHCore.sharedInstance.userProfile) {
            return NO
        }
        
        if (!this._procurement) {
            return NO
        }
        
        if (this._isProcurementOwnedByTheActiveCompany) {
            return NO
        }

        if (this._isViewingProcurementClosedWithoutContractAsOfferer) {
            if (this._isRegisteredAsTenderer && this._makeOfferTabState === "submitted" &&
                (this._offerCapabilities?.canViewDetailed === YES ||
                    (this._makeOfferView?._closedOffers.length ?? 0) > 0)) {
                return YES
            }
            return [...this._roundContext.informationByProcurementId.values()].some(information =>
                information.procurement._id?.toString() !== this._procurement?._id?.toString() &&
                information.offerCapabilities?.canViewDetailed === YES &&
                information.userOffers?.some(offer => offer.status === "submitted") === YES
            )
        }
        
        if (!this._isRegisteredAsTenderer) {
            return NO
        }

        // No offer yet: whether one can be created. An existing draft or
        // submitted offer belongs on this tab only when its details can be viewed;
        // administrator self-add is handled on the separate team tab.
        if (this._makeOfferTabState === "empty") {
            return this._offerCreationCapabilities?.canCreateOffer === YES
        }
        return this._offerCapabilities?.canViewDetailed === YES
    }


    get _shouldShowMakeOfferTab(): boolean {
        return this._shouldShowMakeOfferPanel ||
            this._shouldShowRegisterAsTendererButton ||
            this._shouldShowUnaffiliatedButton ||
            this._shouldShowLoginButton
    }


    get _shouldShowOffererTeamPanel(): boolean {
        if (!this._isInOffererMode || this._isProcurementOwnedByTheActiveCompany || this._isViewingProcurementClosedWithoutContractAsOfferer) {
            return NO
        }

        if (!RHCore.sharedInstance.userProfile || !this._procurement) {
            return NO
        }

        if (!this._isRegisteredAsTenderer) {
            return NO
        }

        if (this._makeOfferTabState === "empty") {
            if (this._offerCreationCapabilities?.canCreateOffer === YES) {
                return YES
            }
            const activeCompanyAffiliation = RHCore.sharedInstance.companyAffiliationStore
                .activeCompanyAffiliation
            return activeCompanyAffiliation?.membershipStatus === "active" &&
                activeCompanyAffiliation.source !== "relationship" &&
                activeCompanyAffiliation.isAuditor !== YES &&
                activeCompanyAffiliation.isAdministrator === YES
        }

        return this._offerCapabilities?.canViewDetailed === YES ||
            (
                this._offerCapabilities?.role === "administrator" &&
                this._offerCapabilities.canAddSelfToTeam === YES
            )
    }


    get _shouldShowTeamPanel(): boolean {
        return this._shouldShowProcurementTeamPanel || this._shouldShowOffererTeamPanel
    }

    get _shouldShowOfferAccessBanner(): boolean {
        if (this._isInOffererMode && this._isProcurementOwnedByTheActiveCompany && RHCore.sharedInstance.userProfile && this._procurement) {
            return YES
        }
        return this._currentTab === "details" && this._isInOffererMode &&
            !!RHCore.sharedInstance.userProfile && !!this._procurement && this._isUserAffiliated &&
            (this._makeOfferTabState === "draft" || this._makeOfferTabState === "submitted") &&
            this._offerCapabilities?.canViewDetailed === NO &&
            !this._hasDetailedOffererTeamAccess && !this._shouldShowOffererTeamPanel && !this._shouldShowMakeOfferPanel
    }


    get _shouldLoadOffererContent(): boolean {
        if (this._isViewingProcurementClosedWithoutContractAsOfferer) {
            return !!RHCore.sharedInstance.userProfile &&
                (this._isRegisteredAsTenderer || this._shouldShowMakeOfferPanel)
        }
        return this._shouldShowMakeOfferPanel || this._shouldShowOffererTeamPanel
    }


    get _isViewingProcurementClosedWithoutContractAsOfferer(): boolean {
        return this._isInOffererMode && this._procurement?.status === "closed-without-contract"
    }
    
    
    private get _shouldShowProcurementTeamPanel(): boolean {
        if (this._isInOffererMode) {
            return NO
        }
        
        if (!RHCore.sharedInstance.userProfile) {
            return NO
        }
        
        if (!this._procurementCapabilities) {
            return NO
        }
        
        if (this._procurementCapabilities.role === "none") {
            return NO
        }
        
        return YES
    }
    
    
    /**
     * Whether the procurer must still confirm they have no conflict of interest for this
     * procurement. While this is YES the server withholds detailed access (canViewDetailed
     * is false), so the Pakkumused and Teabevahetus tabs stay hidden. Confirming
     * (conflictConfirmationButton) flips it and unlocks them.
     */
    private get _shouldShowConflictConfirmation(): boolean {
        if (this._isInOffererMode) {
            return NO
        }

        if (!this._isCurrentUserOnProcuringSide) {
            return NO
        }

        return this._procurementCapabilities?.requiresConflictConfirmation === YES
    }


    /**
     * Whether the cross-company conflict-of-interest banner (server-derived,
     * see conflictOfInterestBannerView) has something to show. Unlike
     * _shouldShowConflictConfirmation this isn't owner-only — any team member
     * with a detected conflict should see it.
     */
    get _shouldShowConflictOfInterestBanner(): boolean {
        if (this._isInOffererMode) {
            return NO
        }

        return this.conflictOfInterestBannerView.isVisible
    }
    
    
    private get _shouldShowIncomingOffersPanel(): boolean {
        if (this._isInOffererMode) {
            return NO
        }
        
        if (!this._isCurrentUserOnProcuringSide) {
            return NO
        }
        
        // Gate on VIEW access (canViewDetailed), NOT on the evaluate / select-winner
        // ACTION capabilities. A procurer must still be able to open the "Pakkumused"
        // tab to view the submitted offers after the winner has been chosen (evaluation
        // finalized), when canEvaluate/canSelectWinner are both NO. Gating on the action
        // capabilities made the offers tab disappear once a winner was selected and made
        // the dashboard "Vaata pakkumusi" link bounce to Üldandmed (via the
        // isTabUnavailable redirect). View-only team members (observers) get the tab too;
        // a "none"-role member with no detailed access still does not.
        return this._hasDetailedProcurementAccess
    }
    
    
    /** Contracts use the same offer-team boundary as the protected offer content. */
    get _shouldShowContractsTab(): boolean {
        if (!this._shouldLoadContracts) {
            return NO
        }
        if (!this._isInOffererMode || this._isCurrentUserOnProcuringSide) {
            return YES
        }
        return this._contractsView?.contractsDataView.data.some(contract =>
            contract.offererCompany._id === RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        ) === YES
    }


    get _shouldLoadContracts(): boolean {
        if (this._roundContext?.hasRounds) {
            return this._roundContext.roundsWithViewableContracts.some(round =>
                round.information.isCurrentUserOnProcuringSide || this._isInOffererMode
            )
        }
        if (!RHProcurementWinnerStatus.hasFinalizedWinningPart(this._procurement)) {
            return NO
        }
        if (this._isCurrentUserOnProcuringSide) {
            return this._hasDetailedProcurementAccess
        }
        return this._hasDetailedOffererTeamAccess
    }
    
    
    private _showTab(tab: ProcurementTab) {
        UIRoute.currentRoute
            .routeBySettingParameterInComponent(
                ProcurementDetailsViewController.routeComponentName,
                ProcurementDetailsViewController.ParameterIdentifierName.tab,
                tab
            )
            .apply()
    }

    _markPendingNotificationAsReadForVisibleContent() {
        if (!RHCore.sharedInstance.notificationStore?.hasPendingNotificationReading) return
        const procurementId = this._contentProcurementId
        const activeCompanyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        const tab = this._currentTab
        const loadGeneration = this._procurementDataLoadGeneration
        requestAnimationFrame(() => {
            if (!procurementId || this._contentProcurementId !== procurementId || this._currentTab !== tab ||
                !this.view.isMemberOfViewTree || this.view.loading || loadGeneration !== this._procurementDataLoadGeneration ||
                RHCore.sharedInstance.companyAffiliationStore.activeCompanyId !== activeCompanyId) return
            let isTabContentVisible = NO
            if (tab === "details") isTabContentVisible = this._detailsView?.isMemberOfViewTree === YES
            if (tab === "team") isTabContentVisible = this._teamView?.isMemberOfViewTree === YES
            if (tab === "contracts") isTabContentVisible = this._contractsView?.isMemberOfViewTree === YES
            const notificationStore = RHCore.sharedInstance.notificationStore
            const detailsSnapshot = this._procurementDetailsNotificationSnapshot
            if (isTabContentVisible && detailsSnapshot?.procurementId === procurementId &&
                detailsSnapshot.companyId === activeCompanyId && detailsSnapshot.loadedTabs.has(tab)) {
                notificationStore.markPendingNotificationAsReadIfContentMatches({ procurementId, tab, auditCutoffDate: detailsSnapshot.auditCutoff })
                if (tab === "contracts" && this._contractsView?.contractsDataView?.isMemberOfViewTree) {
                    for (const contract of this._contractsView.contractsDataView.data) {
                        const roundInformation = this._roundContext.informationByProcurementId.get(contract.procurement._id)
                        if (contract.procurement._id !== procurementId && roundInformation) {
                            notificationStore.markPendingNotificationAsReadIfContentMatches({ procurementId: contract.procurement._id, tab,
                                auditCutoffDate: roundInformation.notificationSnapshotAt })
                        }
                    }
                }
            }
            if (tab === "details" && this._usesRoundWorkspace && this._roundWorkspace?.isMemberOfViewTree &&
                detailsSnapshot?.procurementId === procurementId && detailsSnapshot.companyId === activeCompanyId) {
                for (const [partId, section] of this._roundWorkspace.sectionsByPartId) {
                    const round = this._roundContext.selectedRoundForPart(partId)
                    if (!round || section._round !== round || !(section._content instanceof RHProcurementDetailsView) ||
                        !section._content.isMemberOfViewTree) continue
                    notificationStore.markPendingNotificationAsReadIfContentMatches({ procurementId: round.procurementId, tab,
                        auditCutoffDate: round.information.notificationSnapshotAt })
                }
            }
            if (tab === "qa" && this._qaView?.isMemberOfViewTree && this._qaView.selectedSubtabKey === "public") {
                for (const sourceProcurementId of this._qaView.communicationLabelsByProcurementId.keys()) {
                    notificationStore.markPendingNotificationAsReadIfContentMatches({ procurementId: sourceProcurementId, tab,
                        qaSnapshot: this._qaView.notificationSnapshotForProcurement(sourceProcurementId) })
                }
            }
            if ((tab === "makeOffer" && !this._usesRoundWorkspace && this._makeOfferView?.isMemberOfViewTree) ||
                (tab === "team" && this._makeOfferView?.teamContentView?.isMemberOfViewTree)) {
                const offererNotificationContext = this._offererContentLoadNotificationContext
                const lastMakeOfferViewLoadResult = this._makeOfferView?.lastLoadResult
                if (offererNotificationContext?.procurementId !== procurementId || offererNotificationContext.companyId !== activeCompanyId || !lastMakeOfferViewLoadResult?.offerAccessDate) return
                if (tab === "team") {
                    notificationStore.markPendingNotificationAsReadIfContentMatches({ procurementId, tab, auditCutoffDate: lastMakeOfferViewLoadResult.offerAccessDate })
                    return
                }
                notificationStore.markPendingNotificationAsReadIfContentMatches({ procurementId, tab,
                    eventTypes: ["offer.opened", "offer.notification_sent", "offer.required_documents_changed",
                        "offer.evaluation_reopened", "procurement.reconciliation_invitation_sent"],
                    auditCutoffDate: lastMakeOfferViewLoadResult.offerAccessDate })
                if (offererNotificationContext.partClosureAuditCutoff && lastMakeOfferViewLoadResult.renderedClosedWithoutContractPartIds.length > 0) {
                    notificationStore.markPendingNotificationAsReadIfContentMatches({ procurementId, tab,
                        eventTypes: ["offer.part_closed_without_contract"], auditCutoffDate: offererNotificationContext.partClosureAuditCutoff })
                }
            }
        })
    }


    /** Consumes one-shot private-message parameters from the current loaded procurement route. */
    _consumePrivateMessageDeepLinkForCurrentRoute(procurementId: string) {
        const route = UIRoute.currentRoute
        const routeComponent = route.componentWithName(
            ProcurementDetailsViewController.routeComponentName
        )
        if (
            routeComponent?.parameters.id !== procurementId ||
            routeComponent.parameters.qaSubtab !== "private"
        ) {
            return
        }

        let offererCompanyId: string | undefined
        if (typeof routeComponent.parameters.privateMessageCompanyId === "string") {
            offererCompanyId = routeComponent.parameters.privateMessageCompanyId
        }
        if (this._canSeeQA) {
            this._loadQAView().showPrivateConversation(offererCompanyId)
        }

        route
            .routeByRemovingParameterInComponent(
                ProcurementDetailsViewController.routeComponentName,
                "qaSubtab"
            )
            .routeByRemovingParameterInComponent(
                ProcurementDetailsViewController.routeComponentName,
                "privateMessageCompanyId"
            )
            .applyByReplacingCurrentRouteInHistoryWithoutRouteChangeApproval()
    }
    
    
    private _showAskQuestionDialog() {
        if (!this._procurement?._id) {
            CBDialogViewShower.alert("Viga: hange pole laaditud")
            return
        }
        
        this._updateLifecyclePresentationInDetailsView(this.askQuestionDialog.procurementDetailsView)
        this.askQuestionDialog.showForProcurement(
            this._procurementDataForDetailsView(this._procurement),
            async () => {
                RHCore.sharedInstance.qaStore.invalidate(this._procurement!._id + "")
                await this._loadQAView().loadQuestionsForProcurement(this._procurement!)
                this.view.setNeedsLayoutUpToRootView()
            },
            this._loadQAView()._composeTargets
        )
    }


    private _showProcurerMessageDialog() {
        if (!this._procurement?._id) {
            CBDialogViewShower.alert("Viga: hange pole laaditud")
            return
        }

        this._updateLifecyclePresentationInDetailsView(this.procurerMessageDialog.procurementDetailsView)
        this.procurerMessageDialog.showForProcurement(
            this._procurementDataForDetailsView(this._procurement),
            async () => {
                RHCore.sharedInstance.qaStore.invalidate(this._procurement!._id + "")
                await this._loadQAView().loadQuestionsForProcurement(this._procurement!)
                this.view.setNeedsLayoutUpToRootView()
            },
            this._loadQAView()._composeTargets
        )
    }
    
    
    override async handleRoute(route: UIRoute) {
        await super.handleRoute(route)
        this.tabBar.selectedKey = this._currentTab
        
        const procurementId = route
            .componentWithViewController(ProcurementDetailsViewController)
            ?.parameters.id
        
        // When the user navigates to a different procurement, the lazy tab views
        // contain stale data from the previous one. Tear them down and reload so
        // the new procurement's content is displayed correctly.
        if (procurementId && procurementId !== this._contentProcurementId) {
            this._clearProcurementContent()
            const didLoadQAContent = await this._loadProcurementData()
            
            if (didLoadQAContent === YES && this._currentTab === "qa" && procurementId) {
                this._markQANotificationsAsSeenForProcurement(procurementId)
            }
            if (procurementId) {
                this._markOfferNotificationsSeenIfViewingOffers(procurementId)
            }
            
            this.view.setNeedsLayoutUpToRootView()
            this.view.forEachViewInSubtree(view => view.setNeedsLayout())
            return
        }

        if (this._isViewingProcurementClosedWithoutContractAsOfferer &&
            (this._currentTab === "team" ||
                (this._currentTab === "makeOffer" && !this._shouldShowMakeOfferTab))) {
            this._showTab("details")
            return
        }

        this._configureRoundWorkspaceForCurrentRoute()
        this._updateMakeOfferTabButtonTitle()
        if (procurementId) {
            this._consumePrivateMessageDeepLinkForCurrentRoute(procurementId)
        }
        
        if (this._currentTab === "qa" && procurementId) {
            this._markQANotificationsAsSeenForProcurement(procurementId)
        }
        let offererContentIsReadyForAcknowledgement = YES
        if (
            procurementId &&
            (this._currentTab === "makeOffer" || this._currentTab === "team") &&
            this._isInOffererMode &&
            !this._offererContentLoadNotificationContext
        ) {
            this._makeOfferViewLoadedForCompanyId =
                RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
            offererContentIsReadyForAcknowledgement =
                await this._reloadOffererNotificationContent()
        }
        if (procurementId) {
            if (offererContentIsReadyForAcknowledgement === YES) {
                this._markOfferNotificationsSeenIfViewingOffers(procurementId)
            }
            this._markProcurementClosureNotificationSeenIfViewingDetails(procurementId)
        }

        if (
            this._currentTab === "contracts" &&
            this._shouldShowContractsTab &&
            this._procurement
        ) {
            const detailsSnapshot = this._procurementDetailsNotificationSnapshot
            detailsSnapshot?.loadedTabs.delete("contracts")
            const contractsView = this._loadContractsView()
            const procurement = this._procurement
            await contractsView.loadContractsForProcurement(
                procurement,
                this._isCurrentUserOnProcuringSide,
                this._procurementCapabilities?.canEdit === YES
            )
            if (contractsView.hasLoadedContractsForProcurement(procurement) &&
                detailsSnapshot === this._procurementDetailsNotificationSnapshot) {
                detailsSnapshot?.loadedTabs.add("contracts")
            }
        }
        
        this.view.setNeedsLayoutUpToRootView()
        this.view.forEachViewInSubtree(view => view.setNeedsLayout())
        this._markPendingNotificationAsReadForVisibleContent()
        
    }
    
    
    /**
     * Removes and nulls out all lazily-created tab content views so that the
     * next navigation to a different procurement starts with a clean state.
     * Called from handleRoute when the procurement ID changes.
     */
    _clearProcurementContent() {
        this._contentProcurementId = undefined
        this.procurementClosureBanner.removeFromSuperview()
        this.offerAccessBanner.removeFromSuperview()
        this._roundWorkspace?.removeFromSuperview()
        this._roundWorkspace = undefined
        this._roundContext._loadGeneration += 1
        this._roundContext = new ProcurementRoundContext()
        this._reconciliationRoundChain = []
        this._detailsView?.removeFromSuperview()
        this._detailsView = undefined
        this._makeOfferView?.teamContentView?.removeFromSuperview()
        this._makeOfferView?.removeFromSuperview()
        this._makeOfferView = undefined
        this._offersView?.removeFromSuperview()
        this._offersView = undefined
        this._qaView?.invalidatePrivateCommunicationSummary()
        this._qaView?.removeFromSuperview()
        this._qaView = undefined
        this._contractsView?.removeFromSuperview()
        this._contractsView = undefined
        this._teamView?.removeFromSuperview()
        this._teamView = undefined
        this._procurementTeam = undefined
        this._isApplyingProcurementTeamChange = NO
        this._isAddingCurrentUserToProcurementTeam = NO
        this._makeOfferViewLoadedForCompanyId = undefined
        this._offererContentLoadNotificationContext = undefined
        this._offerResultAcknowledgementKey = undefined
        this._offerPartClosureAcknowledgementKey = undefined
        this._procurementDetailsNotificationSnapshot = undefined
        this._procurementClosureAcknowledgementKey = undefined
        this._offererContentReloadPromise = undefined
        this._offererContentReloadRequested = NO
        this._offererContentLoadGeneration += 1
        this._procurement = undefined
        this._showsProcurementUnavailableState = NO
        this.procurementUnavailableBanner.removeFromSuperview()
        this._procurementCapabilities = undefined
        this._procurementEngagementSummary = undefined
        this._isCurrentUserOnProcuringSide = NO
        this._userOffers = []
        this._offerCapabilities = undefined
        this._offerCreationCapabilities = undefined
        this._canWithdrawTendererRegistration = NO
        this._openingProtocolOfferCount = 0
        this._openingProtocolSharing = undefined
        this._openingProtocolExportsByProcurementId = {}
        this._makeOfferTabState = "empty"
        this._updateMakeOfferTabButtonTitle()
        this._unseenQANotificationCount = 0
        this._unseenOfferNotificationCount = 0
        this._unseenProcurementNotificationCount = 0
        this._requiresProcurerAttention = NO
        this._requiresProcurerQAAttention = NO
    }
    
    
    _updateProcurementActionAvailability() {
        this._loadDetailsView().isOffererView = this._isInOffererMode && !this._isCurrentUserOnProcuringSide
        this._loadDetailsView().canModifyPublishedProcurement = this._canAmendPublishedProcurement
        this._loadDetailsView().canCloseProcurementWithoutContract = this._canCloseProcurementWithoutContract
        this._loadDetailsView().canCloseProcurementPartsWithoutContract = this._canCloseProcurementPartsWithoutContract
        this._loadDetailsView().tendererRegistrationWithdrawalButton.hidden =
            !this._isInOffererMode || !this._canWithdrawTendererRegistration
        // Owner-only "Saada aadressaatidele" in the share dialog (server-authoritative).
        this._loadDetailsView().isCurrentUserOnProcuringSide = this._isCurrentUserOnProcuringSide
    }
    
    
    private _offerSubmissionDeadlineHasPassed(): boolean {
        const deadline = this._procurement?.deadlineDate
        if (!deadline) {
            return NO
        }
        return new Date(deadline) <= new Date()
    }
    

    _isOfferSubmissionClosed(): boolean {
        return this._offerSubmissionDeadlineHasPassed() || this._procurement?.status === "closed-without-contract"
    }

    
    private _updateOfferSubmissionActionStates() {
        const offerSubmissionActionsEnabled = !this._isOfferSubmissionClosed()
        this.registerAsTendererButton.enabled = YES
        this.makeOfferTabButton.enabled = YES
        if (this._makeOfferView) {
            this._makeOfferView.offerActionsEnabled = offerSubmissionActionsEnabled
        }
    }
    
    
    /**
     * True when the current procurer-mode session belongs to the company
     * that published this procurement — the shared eligibility every action
     * that amends or cancels a published procurement requires before its
     * own specific capability check. Both amend and cancel needed this
     * exact block until it was pulled out here.
     */
    get _isProcurerSessionForPublishedProcurement(): boolean {
        if (RHCore.sharedInstance.applicationMode !== "procurer") {
            return NO
        }
        if (!this._isCurrentUserOnProcuringSide) {
            return NO
        }
        if (this._procurement?.status !== "published") {
            return NO
        }

        // The active company can change before the procurement capabilities are refreshed.
        const procuringCompanyReference = this._procurement?.procuringCompany
        let procuringCompanyId: string | undefined
        if (typeof procuringCompanyReference === "string") {
            procuringCompanyId = procuringCompanyReference
        }
        else if (procuringCompanyReference?._id) {
            procuringCompanyId = procuringCompanyReference._id.toString()
        }
        const activeCompanyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        if (activeCompanyId && procuringCompanyId && activeCompanyId !== procuringCompanyId) {
            return NO
        }

        return YES
    }


    get _canAmendPublishedProcurement(): boolean {
        if (!this._isProcurerSessionForPublishedProcurement) {
            return NO
        }
        if (this._procurementCapabilities?.canEdit !== YES) {
            return NO
        }
        if (Boolean(this._procurement?.offersOpenedAt)) {
            return NO
        }
        return YES
    }


    get _canCloseProcurementWithoutContract(): boolean {
        if (!this._isProcurerSessionForPublishedProcurement) {
            return NO
        }
        return this._procurementCapabilities?.canFinalizeOrCancel === YES
    }


    get _canCloseProcurementPartsWithoutContract(): boolean {
        if (!this._canCloseProcurementWithoutContract) {
            return NO
        }
        return (this._procurement?.parts.length ?? 0) > 1
    }


    _routeToPublishedAmendmentEditor(procurementId = this._procurement?._id?.toString()) {
        if (!procurementId) {
            return
        }

        const editorParameters: Record<string, string> = {
            id: procurementId,
            [NewProcurementViewController.ParameterIdentifierName.mode]: "publishedAmendment"
        }
        const currentDetailsComponent = UIRoute.currentRoute.componentWithViewController(
            ProcurementDetailsViewController
        )
        if (currentDetailsComponent?.parameters.id) {
            editorParameters.returnProcurementId = currentDetailsComponent.parameters.id
            editorParameters.returnTab = this._currentTab
            const selectedRound = [...this._roundContext.roundsByPartId.values()].flat().find(round =>
                round.procurementId === procurementId
            )
            if (selectedRound && this._roundContext.hasRounds) {
                editorParameters.returnPartId = selectedRound.rootPartId
                editorParameters.returnRoundId = selectedRound.procurementId
            }
        }
        const backTo = currentDetailsComponent?.parameters.backTo as string | undefined
        if (backTo) {
            editorParameters[NewProcurementViewController.ParameterIdentifierName.backTo] = backTo
        }

        UIRoute.currentRoute
            .routeByRemovingComponentNamed(ProcurementDetailsViewController.routeComponentName)
            .routeWithViewControllerComponent(NewProcurementViewController, editorParameters)
            .apply()
    }
    
    
    _confirmProcurementClosureWithoutContract() {
        this.closureDialog.configureForProcurementClosure()
        this.closureDialog.userDidConfirmClosure = (reason: string) => {
            this.closureDialog.dismiss()
            void this._closeProcurementWithoutContract(reason)
        }
        this.closureDialog.showInRootView(YES)
    }


    _confirmProcurementPartClosureWithoutContract(partId: string) {
        const partNumber = this._procurement?.parts.find(
            part => part._id.toString() === partId
        )?.partNumber
        if (partNumber === undefined) {
            return
        }
        this.closureDialog.configureForProcurementPartClosure(partNumber)
        this.closureDialog.userDidConfirmClosure = (reason: string) => {
            this.closureDialog.dismiss()
            void this._closeProcurementPartWithoutContract(partId, reason)
        }
        this.closureDialog.showInRootView(YES)
    }


    async _closeProcurementWithoutContract(reason: string) {
        if (!this._procurement?._id) {
            return
        }

        CBDialogViewShower.showActionIndicatorDialog("Hanget lõpetatakse ilma lepinguta...")

        try {
            await RHCore.sharedInstance.procurementStore.closeProcurementWithoutContract(
                this._procurement._id.toString(),
                reason
            )
            // The mutation returns only the procurement document, not the
            // server-resolved lifecycle and informative presentation fields.
            await this._loadProcurementData()
        }
        catch (error) {
            CBDialogViewShower.alert(
                "Hanke lõpetamine ilma lepinguta ebaõnnestus: " +
                (messageOfError(error) ?? "Tundmatu viga")
            )
        }
        finally {
            CBDialogViewShower.hideActionIndicatorDialog()
        }
    }


    async _closeProcurementPartWithoutContract(partId: string, reason: string) {
        if (!this._procurement?._id) {
            return
        }

        CBDialogViewShower.showActionIndicatorDialog("Hankeosa lõpetatakse ilma lepinguta...")

        try {
            await RHCore.sharedInstance.procurementStore.closeProcurementPartWithoutContract(
                this._procurement._id.toString(),
                partId,
                reason
            )
            // Closing the final open part can also close the whole procurement.
            await this._loadProcurementData()
        }
        catch (error) {
            CBDialogViewShower.alert(
                "Hankeosa lõpetamine ilma lepinguta ebaõnnestus: " +
                (messageOfError(error) ?? "Tundmatu viga")
            )
        }
        finally {
            CBDialogViewShower.hideActionIndicatorDialog()
        }
    }


    async _openCompanyInvitationDialog(): Promise<void> {
        const procurementId = this._procurement?._id?.toString()
        const companyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        if (!procurementId || !this._canAmendPublishedProcurement || this._offerSubmissionDeadlineHasPassed()) {
            return
        }
        try {
            const procurementInformation = await RHCore.sharedInstance.procurementStore.retrieveProcurementInformation(procurementId)
            if (!this.view.isMemberOfViewTree || this._procurement?._id?.toString() !== procurementId ||
                companyId !== RHCore.sharedInstance.companyAffiliationStore.activeCompanyId ||
                !procurementInformation.isCurrentUserOnProcuringSide || !procurementInformation.capabilities?.canEdit ||
                !isFullProcurement(procurementInformation.procurement)) {
                return
            }
            this._companyInvitationDialog?.dismiss()
            this._companyInvitationDialog = new InviteCompaniesToProcurementDialogView()
            await this._companyInvitationDialog.showForProcurement(procurementInformation.procurement)
        } catch (error) {
            CBDialogViewShower.alert(messageOfError(error) ?? "Ettevõtete kutsumise avamine ebaõnnestus.")
        }
    }

    async _sendProcurementShareEmailToRecipientsWithLink(recipients: string[], link: string): Promise<void> {
        const procurementId = this._procurement?._id?.toString()
        if (!procurementId) {
            const missingProcurementError = new Error("Hange täpsustamata")
            CBDialogViewShower.alert(missingProcurementError.message)
            throw missingProcurementError
        }
        
        CBDialogViewShower.showActionIndicatorDialog("E-kirju saadetakse...")
        
        let shareError: unknown
        try {
            await RHCore.sharedInstance.procurementStore.sendProcurementShareEmail(
                procurementId,
                recipients,
                link
            )
        }
        catch (error) {
            shareError = error
        }
        finally {
            CBDialogViewShower.hideActionIndicatorDialog()
        }
        
        if (shareError) {
            let errorMessage = "Hanke lingi saatmine ebaõnnestus."
            if (shareError instanceof Error && shareError.message) {
                errorMessage = shareError.message
            }
            CBDialogViewShower.alert(errorMessage)
            throw shareError
        }
        
        CBDialogViewShower.alert("Hanke link saadeti adressaatidele.")
    }
    
    
    /**
     * Shows the notification dialog when a deadline change push is received
     * while the user is viewing this procurement as a registered tenderer.
     */
    _showDeadlineChangedNotificationDialog(payload: RHProcurementDeadlineChangedPayload) {
        let description =
            `Hanke "${RHTextSanitizer.escapedHTML(payload.procurementTitle)}" pakkumuse esitamise tähtaega on muudetud. Uus tähtaeg: ${payload.newDeadlineFormatted}.`
        if (payload.wasAutoExtended) {
            description = RHProcurementDeadlineAutoExtensionPresentation
                .offererDescriptionHTMLWithProcurementTitleAndNewDeadlineText(
                    payload.procurementTitle,
                    payload.newDeadlineFormatted
                )
        }
        const shower = CBDialogViewShower.showQuestionDialog(description)
        
        shower.dialogView.view.yesButton.titleLabel.text = "Vaata hanke lehte"
        shower.dialogView.view.noButton.titleLabel.text = "Ignoreeri"
        shower.dialogView.view.noButtonDismissesDialog = YES
        
        shower.yesButtonWasPressed = () => {
            shower.dialogView.dismiss()
            this._showTab("qa")
        }
    }


    _showProcurementAmendedNotificationDialog(payload: RHProcurementAmendedPayload) {
        const shower = CBDialogViewShower.showQuestionDialog(
            RHProcurementAmendmentPresentation.descriptionHTML(payload.procurementTitle)
        )

        shower.dialogView.view.yesButton.titleLabel.text = "Ava teabevahetus"
        shower.dialogView.view.noButton.configureWithObject({
            titleLabel: { text: "Ok" },
            variant: "primary"
        })
        shower.dialogView.view.noButtonDismissesDialog = YES

        shower.yesButtonWasPressed = () => {
            shower.dialogView.dismiss()
            this._showTab("qa")
        }
    }
    
    
    private _updateOpeningProtocolExportRow() {
        const detailsView = this._loadDetailsView()
        if (!this._procurement) {
            detailsView.showsOpeningProtocolExport = NO
            return
        }

        this._configureOpeningProtocolExportInDetailsViewForInformation(detailsView, {
            procurement: this._procurement as RHProcurement & RHProcurementPublic,
            isCurrentUserOnProcuringSide: this._isCurrentUserOnProcuringSide,
            capabilities: this._procurementCapabilities,
            offerCount: this._openingProtocolOfferCount,
            openingProtocolSharing: this._openingProtocolSharing
        })
    }


    _configureOpeningProtocolExportInDetailsViewForInformation(
        detailsView: RHProcurementDetailsView,
        information: Pick<
            RHProcurementInformationResult,
            "procurement" | "isCurrentUserOnProcuringSide" | "capabilities" | "offerCount" | "openingProtocolSharing"
        >
    ) {
        const canViewAsProcurer =
            RHCore.sharedInstance.applicationMode === "procurer" &&
            information.isCurrentUserOnProcuringSide &&
            information.capabilities?.canViewDetailed === YES
        const canViewAsOfferer =
            this._isInOffererMode && information.openingProtocolSharing?.canDownload === YES
        const shouldShowOpeningProtocolExport = canViewAsProcurer || canViewAsOfferer
        detailsView.showsOpeningProtocolExport = shouldShowOpeningProtocolExport
        detailsView.showsOpeningProtocolUnavailableExplanation = NO
        detailsView.openingProtocolShareButton.hidden = YES
        if (!shouldShowOpeningProtocolExport) {
            return
        }

        detailsView.showsOpeningProtocolUnavailableExplanation =
            canViewAsProcurer && !information.openingProtocolSharing?.sharedAt &&
            !information.openingProtocolSharing?.canShare &&
            this._procurementDeadlineHasPassed(information.procurement) &&
            information.offerCount === 0
        if (detailsView.showsOpeningProtocolUnavailableExplanation) {
            return
        }
        this._configureOpeningProtocolExportButton(
            detailsView.openingProtocolExportButtonView,
            information.procurement
        )
        if (canViewAsProcurer && information.openingProtocolSharing) {
            const sharing = information.openingProtocolSharing
            const procurementId = information.procurement._id!.toString()
            const isSharing = this._openingProtocolSharingProcurementIds.has(procurementId)
            let buttonTitle = "Tee pakkujatele nähtavaks"
            if (sharing.sharedAt) {
                buttonTitle = "Pakkujatele nähtav"
            }
            else if (isSharing) {
                buttonTitle = "Jagamine…"
            }
            detailsView.openingProtocolShareButton.configureWithObject({
                hidden: !sharing.canShare && !sharing.sharedAt,
                enabled: sharing.canShare && !isSharing,
                titleLabel: { text: buttonTitle }
            })
            detailsView.openingProtocolSharingWasRequested = () => {
                void this._shareOpeningProtocolForProcurement(procurementId, detailsView)
            }
        }
        detailsView.setNeedsLayoutUpToRootView()
    }


    async _shareOpeningProtocolForProcurement(procurementId: string, detailsView: RHProcurementDetailsView) {
        if (this._openingProtocolSharingProcurementIds.has(procurementId)) {
            return
        }
        this._openingProtocolSharingProcurementIds.add(procurementId)
        detailsView.openingProtocolShareButton.configureWithObject({
            enabled: NO,
            titleLabel: { text: "Jagamine…" }
        })
        try {
            await RHCore.sharedInstance.procurementStore.shareOpeningProtocolForProcurement(procurementId)
            delete this._openingProtocolExportsByProcurementId[procurementId]
        }
        catch (error) {
            let message = "Avamise protokolli jagamine ebaõnnestus."
            if (error instanceof Error && error.message) {
                message = error.message
            }
            CBDialogViewShower.alert(message)
            detailsView.openingProtocolShareButton.configureWithObject({
                enabled: YES,
                titleLabel: { text: "Tee pakkujatele nähtavaks" }
            })
        }
        finally {
            this._openingProtocolSharingProcurementIds.delete(procurementId)
        }
    }
    
    
    private _configureOpeningProtocolExportButton(
        exportButtonView: ProcurementExcelExportButtonView,
        procurement: RHProcurementPublic
    ) {
        const procurementId = procurement._id?.toString() ?? ""
        const exportData = this._openingProtocolExportsByProcurementId[procurementId]
        let state: RHProcurementExcelExportButtonState = "generate"
        
        if (this._openingProtocolExportLoadingProcurementIds.has(procurementId)) {
            state = "loading"
        }
        else if (!this._procurementDeadlineHasPassed(procurement)) {
            state = "locked"
        }
        else if (!this._procurementOffersHaveBeenOpened(procurement)) {
            state = "locked"
        }
        
        let fileName = ""
        if (exportData) {
            fileName = exportData.fileName
        }
        exportButtonView.configure(state, fileName)
        exportButtonView.exportWasRequested = () => {
            if (!this._procurementDeadlineHasPassed(procurement)) {
                CBDialogViewShower.alert("Avamisprotokolli saab koostada pärast pakkumuste esitamise tähtaega")
                return
            }
            if (!this._procurementOffersHaveBeenOpened(procurement)) {
                CBDialogViewShower.alert("Avamisprotokolli saab koostada pärast pakkumuste avamist")
                return
            }
            this._generateOpeningProtocolExportForProcurement(procurement, exportButtonView)
        }
    }
    
    
    async _generateOpeningProtocolExportForProcurement(
        procurement: RHProcurementPublic,
        exportButtonView: ProcurementExcelExportButtonView
    ) {
        const procurementId = procurement._id?.toString()
        if (!procurementId || this._openingProtocolExportLoadingProcurementIds.has(procurementId)) {
            return
        }
        
        if (!this._procurementDeadlineHasPassed(procurement)) {
            CBDialogViewShower.alert("Avamisprotokolli saab koostada pärast pakkumuste esitamise tähtaega")
            return
        }
        
        if (!this._procurementOffersHaveBeenOpened(procurement)) {
            CBDialogViewShower.alert("Avamisprotokolli saab koostada pärast pakkumuste avamist")
            return
        }
        
        this._openingProtocolExportLoadingProcurementIds.add(procurementId)
        this._configureOpeningProtocolExportButton(exportButtonView, procurement)
        CBDialogViewShower.showActionIndicatorDialog("Väljundit genereeritakse")
        
        let generationError: unknown
        const applicationMode = RHCore.sharedInstance.applicationMode
        const companyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        const loadedProcurementId = this._contentProcurementId
        try {
            let exportData: RHProcurementExcelExport
            if (applicationMode === "offerer") {
                exportData = await RHCore.sharedInstance.procurementStore
                    .retrieveSharedOpeningProtocolForProcurement(procurementId)
            }
            else {
                exportData = await RHCore.sharedInstance.procurementStore.exportProcurementExcel(procurementId)
            }
            if (RHCore.sharedInstance.applicationMode !== applicationMode ||
                RHCore.sharedInstance.companyAffiliationStore.activeCompanyId !== companyId ||
                this._contentProcurementId !== loadedProcurementId) {
                return
            }
            this._openingProtocolExportsByProcurementId[procurementId] = exportData
            this._configureOpeningProtocolExportButton(exportButtonView, procurement)
            ProcurementExcelExportDownloader.download(exportData)
        }
        catch (error) {
            console.error("ProcurementDetailsViewController: error generating opening protocol export:", error)
            generationError = error
        }
        finally {
            this._openingProtocolExportLoadingProcurementIds.delete(procurementId)
            CBDialogViewShower.hideActionIndicatorDialog()
            this._configureOpeningProtocolExportButton(exportButtonView, procurement)
        }
        
        if (generationError) {
            let errorMessage = "Avamisprotokolli koostamine ebaõnnestus."
            if (generationError instanceof Error && generationError.message) {
                errorMessage = generationError.message
            }
            CBDialogViewShower.alert(errorMessage)
        }
    }
    
    
    private _procurementDeadlineHasPassed(procurement: RHProcurementPublic): boolean {
        if (!procurement.deadlineDate) {
            return NO
        }
        return new Date(procurement.deadlineDate) <= new Date()
    }
    
    
    _procurementOffersHaveBeenOpened(procurement: RHProcurementPublic): boolean {
        if (!procurement.offersOpenedAt) {
            return NO
        }
        return new Date(procurement.offersOpenedAt).getTime() <= Date.now()
    }
    
    
    // ─── Layout ───────────────────────────────────────────────────────────────
    
    override layoutViewSubviews() {
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const buttonHeight = RHButtons.compactHeight
        
        const contentBounds = this.view.contentBoundsWithInset(padding)
        let bounds = contentBounds
            .IF(this._currentTab === "qa")
            .rectangleWithMaxWidth(RHProcurementDetailsViewTokens.qaContentMaxWidth, 0.5)
            .ELSE()
            .rectangleWithMaxWidth(RHProcurementDetailsViewTokens.contentMaxWidth, 0.5)
            .ENDIF()

        let shouldShowSideBySide = NO
        // /// #if DEV
        // bounds = this.view.contentBoundsWithInset(padding)
        // shouldShowSideBySide = bounds.width >= (this.sidePanelWidth * 2 + padding * 3)
        // /// #endif
        
        if (this._isViewingProcurementClosedWithoutContractAsOfferer && !this._showsProcurementUnavailableState) {
            this.procurementClosureBanner.addedAsSubviewToView(this.view)
        }
        else {
            this.procurementClosureBanner.removeFromSuperview()
        }

        if (this._showsProcurementUnavailableState) {
            this._updateSubviewsForUnavailableProcurement()
        }
        else {
            this.procurementUnavailableBanner.removeFromSuperview()
            if (shouldShowSideBySide) {
                this._updateSubviewsForSideBySideLayout()
            }
            else {
                this._updateSubviewsForTabbedLayout()
            }
        }

        if (!this._showsProcurementUnavailableState && this._shouldShowOfferAccessBanner) {
            if (this._isProcurementOwnedByTheActiveCompany) {
                this.offerAccessBanner.textAlignment = UITextView.textAlignment.center
                this.offerAccessBanner.text = "See on sinu ettevõtte hange. Oma ettevõtte hankele ei saa pakkujana registreeruda, küsimusi esitada ega pakkumust teha."
            }
            else {
                this.offerAccessBanner.textAlignment = UITextView.textAlignment.left
                const offerStatusText = IF(this._makeOfferTabState === "draft")(() =>
                    "Sinu ettevõttel on sellel hankel pakkumus koostamisel."
                ).ELSE(() => "Sinu ettevõte on sellele hankele pakkumuse esitanud.")
                this.offerAccessBanner.text = offerStatusText +
                    " Näed ainult hanke üldandmeid, sest Sa ei kuulu selle pakkuja meeskonda. " +
                    "Pakkumuse, meeskonna ja teabevahetuse nägemiseks palu pakkumuse vastutaval kasutajal või " +
                    "ettevõtte peakasutajal end pakkuja meeskonda lisada."
            }
            this.offerAccessBanner.addedAsSubviewToView(this.view)
        }
        else {
            this.offerAccessBanner.removeFromSuperview()
        }

        this._updateBreadcrumbs()

        const titleFrame = contentBounds
            .rectangleWithHeight(this.breadcrumbView)
            .assignedAsFrameOfView(this.breadcrumbView)
            .rectangleForNextRow(padding, 0)
            .rectangleWithX(bounds.x)
            .rectangleWithWidth(bounds.width)
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)

        const backButtonFrame = titleFrame
            .rectangleForNextRow(padding, buttonHeight)
            .rectangleWithWidth(RHButtons.pageBackButtonWidth)
            .assignedAsFrameOfView(this.backButton)

        bounds
            .IF(this._showsProcurementUnavailableState)
            .rectangleWithY(backButtonFrame.max.y + padding * 2)
            .rectangleWithHeight(this.procurementUnavailableBanner)
            .assignedAsFrameOfView(this.procurementUnavailableBanner)
            .rectangleForNextRow(padding, this.procurementAccessExplanationLabel)
            .assignedAsFrameOfView(this.procurementAccessExplanationLabel)
            .IF(this.loginButton.superview === this.view)
            .rectangleForNextRow(padding, buttonHeight)
            .assignedAsFrameOfView(this.loginButton)
            .ELSE()
            .rectangleForNextRow(padding, buttonHeight)
            .assignedAsFrameOfView(this.unaffiliatedButton)
            .ENDIF()
            .ENDIF()

        if (this._showsProcurementUnavailableState) {
            return
        }

        if (shouldShowSideBySide) {
            this._layoutSideBySide(bounds, padding, buttonHeight)
        }
        else {
            this._layoutTabbed(bounds, padding, buttonHeight)
        }
    }


    _updateSubviewsForUnavailableProcurement() {
        this.procurementUnavailableBanner.addedAsSubviewToView(this.view)

        const unavailableContentViews: UIView[] = [
            this.tabBar,
            this.registerAsTendererButton,
            this.unaffiliatedExplanationLabel,
            this.unaffiliatedButton,
            this.loginExplanationLabel,
            this.loginButton,
            this.conflictConfirmationBanner,
            this.conflictConfirmationButton,
            this.conflictOfInterestBannerView,
            this.askQuestionButton,
            this.sendMessageButton
        ]
        for (const unavailableContentView of unavailableContentViews) {
            unavailableContentView.removeFromSuperview()
        }
        this.procurementAccessExplanationLabel.addedAsSubviewToView(this.view)
        if (RHCore.sharedInstance.userProfile) {
            this.unaffiliatedButton.addedAsSubviewToView(this.view)
        } else {
            this.loginButton.addedAsSubviewToView(this.view)
        }

        this._detailsView?.removeFromSuperview()
        this._teamView?.removeFromSuperview()
        this._makeOfferView?.teamContentView?.removeFromSuperview()
        this._makeOfferView?.removeFromSuperview()
        this._offersView?.removeFromSuperview()
        this._qaView?.removeFromSuperview()
        this._contractsView?.removeFromSuperview()
    }
    
    
    private _updateSubviewsForSideBySideLayout() {
        this.tabBar.removeFromSuperview()
        this._teamView?.removeFromSuperview()
        this._makeOfferView?.teamContentView?.removeFromSuperview()
        
        this._loadDetailsView().addedAsSubviewToView(this.view)
        
        if (this._canSeeQA) {
            this._loadQAView().addedAsSubviewToView(this.view)
        }
        else {
            this._qaView?.removeFromSuperview()
        }
        
        if (this._canAskQuestion) {
            this.askQuestionButton.addedAsSubviewToView(this.view)
        }
        else {
            this.askQuestionButton.removeFromSuperview()
        }
        
        if (this._shouldShowRegisterAsTendererButton) {
            this.registerAsTendererButton.addedAsSubviewToView(this.view)
        }
        else {
            this.registerAsTendererButton.removeFromSuperview()
        }
        
        if (this._shouldShowLoginButton) {
            this.loginExplanationLabel.addedAsSubviewToView(this.view)
            this.loginButton.addedAsSubviewToView(this.view)
        }
        else {
            this.loginExplanationLabel.removeFromSuperview()
            this.loginButton.removeFromSuperview()
        }
        
        if (this._shouldShowMakeOfferPanel) {
            this._loadMakeOfferView().addedAsSubviewToView(this.view)
        }
        else {
            this._makeOfferView?.removeFromSuperview()
        }
        
        if (this._shouldShowIncomingOffersPanel) {
            this._loadOffersView().addedAsSubviewToView(this.view)
        }
        else {
            this._offersView?.removeFromSuperview()
        }
        this.view.setNeedsLayoutUpToRootView()
    }
    
    
    private _updateSubviewsForTabbedLayout() {
        this.tabBar.addedAsSubviewToView(this.view)
        this.detailsTabButton.hidden = NO
        this.teamTabButton.hidden = !this._shouldShowTeamPanel
        let teamTitle = "Hankemeeskond"
        if (this._isInOffererMode) {
            teamTitle = "Pakkuja meeskond"
        }
        this.teamTabButton.titleLabel.text = teamTitle
        this.makeOfferTabButton.hidden = !this._shouldShowMakeOfferTab
        this.offersTabButton.hidden = !this._shouldShowIncomingOffersPanel
        this.qaTabButton.hidden = !this._canSeeQA
        this.contractsTabButton.hidden = !this._shouldShowContractsTab
        this.tabBar.setNeedsLayout()
        
        const currentTab = this._currentTab
        const shouldShowOffererAccessPrompt = currentTab === "makeOffer" || currentTab === "qa"

        const showDetails = currentTab === "details" && !this._usesRoundWorkspace
        const shouldShowTeam = currentTab === "team" && this._shouldShowTeamPanel
        const showMakeOffer = currentTab === "makeOffer" && this._shouldShowMakeOfferPanel && !this._usesRoundWorkspace
        const showOffers = currentTab === "offers" && this._shouldShowIncomingOffersPanel && !this._usesRoundWorkspace
        const showQA = currentTab === "qa" && this._canSeeQA && (this._isProcurementOwnedByTheActiveCompany || this._isRegisteredAsTenderer)
        const showContracts = currentTab === "contracts" && this._shouldShowContractsTab
        
        if (this._usesRoundWorkspace) {
            this._roundWorkspace?.addedAsSubviewToView(this.view)
        }
        else {
            this._roundWorkspace?.removeFromSuperview()
        }
        if (showDetails) {
            this._loadDetailsView().addedAsSubviewToView(this.view)
        }
        else {
            this._detailsView?.removeFromSuperview()
        }
        
        if (shouldShowTeam) {
            if (this._isInOffererMode) {
                this._teamView?.removeFromSuperview()
                this._makeOfferView?.teamContentView?.addedAsSubviewToView(this.view)
            }
            else {
                this._makeOfferView?.teamContentView?.removeFromSuperview()
                this._loadTeamView().addedAsSubviewToView(this.view)
            }
        }
        else {
            this._teamView?.removeFromSuperview()
            this._makeOfferView?.teamContentView?.removeFromSuperview()
        }
        
        if (showMakeOffer) {
            this._loadMakeOfferView().addedAsSubviewToView(this.view)
        }
        else {
            this._makeOfferView?.removeFromSuperview()
        }
        
        if (showOffers) {
            this._loadOffersView().addedAsSubviewToView(this.view)
        }
        else {
            this._offersView?.removeFromSuperview()
        }
        
        if (showQA) {
            this._loadQAView().addedAsSubviewToView(this.view)
        }
        else {
            this._qaView?.removeFromSuperview()
        }
        
        if (showContracts) {
            this._loadContractsView().addedAsSubviewToView(this.view)
        }
        else {
            this._contractsView?.removeFromSuperview()
        }
        
        if (showQA && this._canAskQuestion) {
            this.askQuestionButton.addedAsSubviewToView(this.view)
        }
        else {
            this.askQuestionButton.removeFromSuperview()
        }
        
        if (showQA && this._canCurrentUserSendProcurerMessage) {
            this.sendMessageButton.addedAsSubviewToView(this.view)
        }
        else {
            this.sendMessageButton.removeFromSuperview()
        }
        
        // In the tabbed layout the green tab is the single registration/offer
        // action. The standalone button is reserved for the side-by-side layout,
        // where the tab bar itself is not rendered.
        this.registerAsTendererButton.removeFromSuperview()
        
        if (shouldShowOffererAccessPrompt && this._shouldShowUnaffiliatedButton) {
            this.unaffiliatedExplanationLabel.addedAsSubviewToView(this.view)
            this.unaffiliatedButton.addedAsSubviewToView(this.view)
        }
        else {
            this.unaffiliatedExplanationLabel.removeFromSuperview()
            this.unaffiliatedButton.removeFromSuperview()
        }
        
        if (shouldShowOffererAccessPrompt && this._shouldShowLoginButton) {
            this.loginExplanationLabel.addedAsSubviewToView(this.view)
            this.loginButton.addedAsSubviewToView(this.view)
        }
        else {
            this.loginExplanationLabel.removeFromSuperview()
            this.loginButton.removeFromSuperview()
        }
        
        if (this._shouldShowConflictConfirmation) {
            this.conflictConfirmationBanner.addedAsSubviewToView(this.view)
            this.conflictConfirmationButton.addedAsSubviewToView(this.view)
        }
        else {
            this.conflictConfirmationBanner.removeFromSuperview()
            this.conflictConfirmationButton.removeFromSuperview()
        }

        if (this._shouldShowConflictOfInterestBanner) {
            this.conflictOfInterestBannerView.addedAsSubviewToView(this.view)
        }
        else {
            this.conflictOfInterestBannerView.removeFromSuperview()
        }

        this.tabBar.selectedKey = currentTab
        this.view.setNeedsLayoutUpToRootView()
    }
    
    
    private _layoutSideBySide(bounds: UIRectangle, padding: number, buttonHeight: number) {
        const [leftPanel, rightPanel] = bounds
            .rectangleWithY(this.backButton.frame.max.y + padding * 2)
            .rectanglesBySplittingWidth([1, 1], padding)
        
        const leftPanelViews: UIView[] = []
        if (this.registerAsTendererButton.superview === this.view) {
            leftPanelViews.push(this.registerAsTendererButton)
        }
        if (this._isViewingProcurementClosedWithoutContractAsOfferer) {
            leftPanelViews.push(this.procurementClosureBanner)
        }
        if (this.offerAccessBanner.superview === this.view) {
            leftPanelViews.push(this.offerAccessBanner)
        }
        leftPanelViews.push(this._loadDetailsView())
        if (this.unaffiliatedExplanationLabel.superview === this.view) {
            leftPanelViews.push(this.unaffiliatedExplanationLabel)
            leftPanelViews.push(this.unaffiliatedButton)
        }
        if (this.loginExplanationLabel.superview === this.view) {
            leftPanelViews.push(this.loginExplanationLabel)
            leftPanelViews.push(this.loginButton)
        }
        if (this._shouldShowMakeOfferPanel) {
            leftPanelViews.push(this._loadMakeOfferView())
        }
        if (this._shouldShowIncomingOffersPanel) {
            leftPanelViews.push(this._loadOffersView())
        }
        leftPanel.framesByDistributingViewsAsColumn(leftPanelViews, padding)
        this._centerPromptActionButtonIfVisible(this.registerAsTendererButton)
        this._centerPromptActionButtonIfVisible(this.unaffiliatedButton)
        this._centerPromptActionButtonIfVisible(this.loginButton)
        
        if (this._canSeeQA) {
            const qaView = this._loadQAView()
            rightPanel
                .rectangleWithHeight(0)
                .IF(this.askQuestionButton.superview == this.view)
                .rectangleWithHeight(RHButtons.compactHeight)
                .assignedAsFrameOfView(this.askQuestionButton)
                .ENDIF()
                .IF(this.sendMessageButton.superview == this.view)
                .rectangleWithHeight(RHButtons.compactHeight)
                .assignedAsFrameOfView(this.sendMessageButton)
                .ENDIF()
                .rectangleForNextRow(padding, qaView)
                .assignedAsFrameOfView(qaView)
            this._centerQaActionButtonIfVisible(this.askQuestionButton)
            this._centerQaActionButtonIfVisible(this.sendMessageButton)
        }
    }
    
    
    private _promptActionButtonWidth(button: RHButton, availableWidth: number): number {
        return [
            [
                button.titleLabel.intrinsicContentWidth(RHButtons.compactHeight) +
                RHButtons.compactContentPadding * 2 +
                RHSpacing.xLarge,
                RHProcurementDetailsViewTokens.promptActionButtonMinimumWidth
            ].max(),
            RHProcurementDetailsViewTokens.promptActionButtonMaximumWidth,
            availableWidth
        ].min()
    }
    
    
    private _centerPromptActionButtonIfVisible(button: RHButton) {
        if (button.superview !== this.view) {
            return
        }
        
        const buttonWidth = this._promptActionButtonWidth(button, button.frame.width)
        button.frame = button.frame.rectangleWithWidth(buttonWidth, 0.5)
    }
    
    
    private _qaActionButtonWidth(button: RHButton, availableWidth: number): number {
        return [
            [
                button.titleLabel.intrinsicContentWidth(
                    RHButtons.compactHeight
                ) +
                RHButtons.compactContentPadding * 2 +
                RHSpacing.xLarge,
                240
            ].max(),
            320,
            availableWidth
        ].min()
    }
    
    
    // Centers whichever Q&A action button is currently visible (askQuestion / sendMessage).
    private _centerQaActionButtonIfVisible(button: RHButton) {
        if (button.superview !== this.view) {
            return
        }
        
        const buttonWidth = this._qaActionButtonWidth(button, button.frame.width)
        button.frame = button.frame.rectangleWithWidth(buttonWidth, 0.5)
    }
    
    
    private _layoutTabbed(bounds: UIRectangle, padding: number, buttonHeight: number) {
        const tabButtonsFrame = bounds
            .rectangleWithY(this.backButton.frame.max.y + padding * 2)
            .rectangleWithHeight(buttonHeight)
            .assignedAsFrameOfView(this.tabBar)
        
        const currentTab = this._currentTab
        const detailsView = this._loadDetailsView()
        
        // Resolve only the views that already exist — never call _load* here,
        // as all ELSE_IF arguments are evaluated eagerly before the chain runs.
        const makeOfferView = this._makeOfferView
        let teamView: UIView | undefined = this._teamView
        if (this._isInOffererMode) {
            teamView = this._makeOfferView?.teamContentView
        }
        const offersView = this._offersView
        const qaView = this._qaView
        const contractsView = this._contractsView
        
        const isLoginMessageVisible = this.loginButton.superview === this.view
        
        // Access prompts stay above tab content so the required action is never below the fold.
        const contentAnchorFrame = tabButtonsFrame
            .IF(this.offerAccessBanner.superview === this.view)
            .rectangleForNextRow(padding, this.offerAccessBanner)
            .assignedAsFrameOfView(this.offerAccessBanner)
            .ENDIF()
            .IF(this._shouldShowConflictConfirmation)
            .rectangleForNextRow(padding, this.conflictConfirmationBanner)
            .assignedAsFrameOfView(this.conflictConfirmationBanner)
            .rectangleForNextRow(padding, buttonHeight)
            .assignedAsFrameOfView(this.conflictConfirmationButton)
            .ENDIF()
            .IF(this._shouldShowConflictOfInterestBanner)
            .rectangleForNextRow(padding, this.conflictOfInterestBannerView)
            .assignedAsFrameOfView(this.conflictOfInterestBannerView)
            .ENDIF()
            .IF(this.unaffiliatedButton.superview === this.view)
            .rectangleForNextRow(padding * 2, this.unaffiliatedExplanationLabel)
            .assignedAsFrameOfView(this.unaffiliatedExplanationLabel)
            .rectangleForNextRow(padding, buttonHeight)
            .assignedAsFrameOfView(this.unaffiliatedButton)
            .ENDIF()
            .IF(isLoginMessageVisible)
            .rectangleForNextRow(padding * 2, this.loginExplanationLabel)
            .assignedAsFrameOfView(this.loginExplanationLabel)
            .rectangleForNextRow(padding, buttonHeight)
            .assignedAsFrameOfView(this.loginButton)
            .ENDIF()
            .IF(this._isViewingProcurementClosedWithoutContractAsOfferer)
            .rectangleForNextRow(padding, this.procurementClosureBanner)
            .assignedAsFrameOfView(this.procurementClosureBanner)
            .ENDIF()

        this._centerPromptActionButtonIfVisible(this.conflictConfirmationButton)
        this._centerPromptActionButtonIfVisible(this.unaffiliatedButton)
        this._centerPromptActionButtonIfVisible(this.loginButton)

        contentAnchorFrame
            .IF(this._usesRoundWorkspace && !!this._roundWorkspace)
            .rectangleForNextRow(padding, this._roundWorkspace!)
            .assignedAsFrameOfView(this._roundWorkspace!)
            .ELSE_IF(currentTab === "details")
            .rectangleForNextRow(padding, detailsView)
            .assignedAsFrameOfView(detailsView)
            .ELSE_IF(currentTab === "team" && this._shouldShowTeamPanel && teamView != null)
            .rectangleForNextRow(padding, teamView!)
            .assignedAsFrameOfView(teamView!)
            .ELSE_IF(currentTab === "makeOffer" && this._shouldShowMakeOfferPanel && makeOfferView != null)
            .rectangleForNextRow(padding, makeOfferView!)
            .assignedAsFrameOfView(makeOfferView!)
            .ELSE_IF(currentTab === "offers" && this._shouldShowIncomingOffersPanel && offersView != null)
            .rectangleForNextRow(padding, offersView!)
            .assignedAsFrameOfView(offersView!)
            .ELSE_IF(
                currentTab === "qa" && this._canSeeQA && (this._isProcurementOwnedByTheActiveCompany || this._isRegisteredAsTenderer) && qaView != null)
            .IF(this.askQuestionButton.superview == this.view)
            .rectangleForNextRow(padding, RHButtons.compactHeight)
            .assignedAsFrameOfView(this.askQuestionButton)
            .ENDIF()
            .IF(this.sendMessageButton.superview == this.view)
            .rectangleForNextRow(padding, RHButtons.compactHeight)
            .assignedAsFrameOfView(this.sendMessageButton)
            .ENDIF()
            .rectangleForNextRow(padding, qaView!)
            .assignedAsFrameOfView(qaView!)
            .ELSE_IF(currentTab === "contracts" && this._shouldShowContractsTab && contractsView != null)
            .rectangleForNextRow(padding, contractsView!)
            .assignedAsFrameOfView(contractsView!)
            .ENDIF()
        
        this._centerQaActionButtonIfVisible(this.askQuestionButton)
        this._centerQaActionButtonIfVisible(this.sendMessageButton)
    }
    
}
